/**
 * Tab de transacciones del mes.
 *
 * 3 flujos dedicados (decisión 2026-05-10): Ingreso, Gasto y Transferencia
 * tienen forms independientes porque comparten muy poco — un ingreso no usa
 * tarjeta de crédito, un gasto no genera factura emitida, una transferencia
 * no tiene IVA ni categoría. Mezclarlos en un solo modal era ruidoso.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch, Controller, type Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Trash2,
  Receipt,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  ArrowLeftRight,
  ArrowDownLeft,
  ArrowUpRight,
  Paperclip,
  X,
  SlidersHorizontal,
  Search,
  Pencil,
  GitBranch,
  MoreHorizontal,
  Building2,
  UserCheck,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Percent,
  Calculator,
  Wallet,
  FileSpreadsheet,
  CheckCircle2,
  Package,
} from "lucide-react";
import clsx from "clsx";
import {
  transactionCreateSchema,
  transferCreateSchema,
  ISR_RATE_RESICO_PM,
  PAYMENT_METHODS,
  type TransactionCreateInput,
  type TransferCreateInput,
  type Transaction,
  type Account,
  type PaymentMethod,
} from "@axorial/shared";
import { api } from "@/lib/api";
import { formatDate, formatMxnCents } from "@/lib/format";
import { PeriodBalancesStrip } from "@/features/finance/monthlyBalances";
import { calcIsrAsimiladoMensual, calcBrutoFromNetoAsimilado } from "@/lib/isr";
import { CompanyPicker } from "@/features/projects/CompanyPicker";
import {
  type DocKind,
  type DocKindUi,
  DOC_KIND_LABEL,
  DOC_KIND_UI_LABEL,
  DOC_KINDS,
  DOC_KINDS_UI,
  guessDocKindUi,
  uiKindToKind,
} from "@/features/finance/docKinds";
import { exportTransactionsToExcel } from "@/features/finance/exportExcel";
import { exportFinancePackageZip } from "@/features/finance/exportPackage";

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  transfer: "Transferencia",
  card_credit: "Tarjeta de crédito",
  card_debit: "Tarjeta de débito",
  cash: "Efectivo",
};

interface Category {
  id: string;
  name: string;
  kind: "ingreso" | "gasto";
  group: string | null;
  ivaApplicable: boolean;
}

interface Transfer {
  id: string;
  fromAccountId: string;
  toAccountId: string;
  amountCents: number;
  date: string;
  affectsTotals: boolean;
  notes: string | null;
  /** Saldo de cada cuenta DESPUÉS de esta transferencia (lo computa fin-api). */
  fromBalanceAfterCents: number | null;
  toBalanceAfterCents: number | null;
}

type ModalKind = null | "income" | "expense" | "transfer";

interface Filters {
  from: string;
  to: string;
  q: string;
  kind: "" | "ingreso" | "gasto";
  accountId: string;
  categoryId: string;
  paymentStatus: "" | "cobrado" | "pagado" | "pendiente";
  invoiceStatus:
    | ""
    | "facturado"
    | "deducible_sin_factura"
    | "pendiente"
    | "no_aplica"
    | "olvidado";
  affectsTotals: "" | "1" | "0";
  hasAttachment: "" | "1" | "0";
}

function defaultFilters(_period: string): Filters {
  // Estado base: sin restricción de fechas ni filtros. Es lo que aplica al
  // botón "Limpiar todo" y la línea base para contar filtros activos.
  return {
    from: "",
    to: "",
    q: "",
    kind: "",
    accountId: "",
    categoryId: "",
    paymentStatus: "",
    invoiceStatus: "",
    affectsTotals: "",
    hasAttachment: "",
  };
}

/**
 * Estado inicial al abrir la pestaña: "Mes actual" (del día 1 a hoy). Es
 * un filtro real — no el default — así el badge de "Filtros · 1" lo señala
 * y el usuario sabe que no está viendo todo el histórico.
 */
function initialFilters(): Filters {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const monthStart = `${y}-${String(m + 1).padStart(2, "0")}-01`;
  const todayIso = `${y}-${String(m + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  return {
    ...defaultFilters(""),
    from: monthStart,
    to: todayIso,
  };
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

/**
 * Atajos de rango basados en HOY (no en el header). "Mes actual" = del 1
 * del mes a hoy ("lo que va del mes"). "Mes anterior" = el mes completo
 * anterior. "Trimestre" / "YTD" = desde el inicio del periodo a hoy.
 * "Esta semana" = lunes a hoy (semana ISO).
 */
type ShortcutKind = "week" | "month" | "lastMonth" | "quarter" | "ytd" | "none";

function rangeShortcut(kind: "week" | "month" | "lastMonth" | "quarter" | "ytd"): {
  from: string;
  to: string;
} {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (kind) {
    case "week": {
      const day = today.getDay(); // 0=dom, 1=lun, ..., 6=sab
      const offset = day === 0 ? -6 : 1 - day;
      const monday = new Date(today);
      monday.setDate(today.getDate() + offset);
      return { from: isoDate(monday), to: isoDate(today) };
    }
    case "month":
      return { from: isoDate(new Date(y, m, 1)), to: isoDate(today) };
    case "lastMonth": {
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0); // último día del mes anterior
      return { from: isoDate(start), to: isoDate(end) };
    }
    case "quarter": {
      const q = Math.floor(m / 3);
      return { from: isoDate(new Date(y, q * 3, 1)), to: isoDate(today) };
    }
    case "ytd":
      return { from: isoDate(new Date(y, 0, 1)), to: isoDate(today) };
  }
}

/**
 * Rango corte-a-corte para una TDC con día de corte: del día de corte de
 * un mes al del siguiente (así lo presenta el estado de cuenta, ej. Konfio
 * "del 17 mar al 17 abr"). Si el mes no tiene ese día, el corte cae el
 * último día del mes. offset 0 = periodo en curso (cierra en el próximo
 * corte); -1 = último periodo cerrado; -2 = el anterior; etc.
 */
function cutRange(cutDay: number, offset: number): { from: string; to: string } {
  const todayIso = isoDate(new Date());
  const cutOf = (monthIndex: number) => {
    // monthIndex = año*12 + (mes-1), para poder sumar/restar meses libre.
    const y = Math.floor(monthIndex / 12);
    const m = monthIndex - y * 12 + 1;
    const lastDay = new Date(y, m, 0).getDate();
    return `${y}-${String(m).padStart(2, "0")}-${String(Math.min(cutDay, lastDay)).padStart(2, "0")}`;
  };
  const today = new Date();
  let end = today.getFullYear() * 12 + today.getMonth();
  // El periodo en curso cierra en el primer corte estrictamente > hoy.
  if (cutOf(end) <= todayIso) end += 1;
  return { from: cutOf(end + offset - 1), to: cutOf(end + offset) };
}

/**
 * Cuál atajo de rango está activo según el `from`/`to` actual. "none" cuando
 * el rango no coincide con ningún atajo (ej. el usuario tocó las fechas a
 * mano), o cuando ambos están vacíos (= "Sin restricción de fecha").
 */
function activeShortcut(filters: { from: string; to: string }): ShortcutKind | null {
  if (!filters.from && !filters.to) return "none";
  for (const k of ["week", "month", "lastMonth", "quarter", "ytd"] as const) {
    const r = rangeShortcut(k);
    if (r.from === filters.from && r.to === filters.to) return k;
  }
  return null;
}

function buildQueryString(f: Filters): string {
  const params = new URLSearchParams();
  if (f.from) params.set("from", f.from);
  if (f.to) params.set("to", f.to);
  if (f.q) params.set("q", f.q);
  if (f.kind) params.set("kind", f.kind);
  if (f.accountId) params.set("account", f.accountId);
  if (f.categoryId) params.set("category", f.categoryId);
  if (f.paymentStatus) params.set("paymentStatus", f.paymentStatus);
  if (f.invoiceStatus) params.set("invoiceStatus", f.invoiceStatus);
  if (f.affectsTotals) params.set("affectsTotals", f.affectsTotals);
  if (f.hasAttachment) params.set("hasAttachment", f.hasAttachment);
  return params.toString();
}

function activeFilterCount(f: Filters, period: string): number {
  const def = defaultFilters(period);
  let n = 0;
  if (f.from !== def.from || f.to !== def.to) n += 1;
  if (f.q) n += 1;
  if (f.kind) n += 1;
  if (f.accountId) n += 1;
  if (f.categoryId) n += 1;
  if (f.paymentStatus) n += 1;
  if (f.invoiceStatus) n += 1;
  if (f.affectsTotals) n += 1;
  if (f.hasAttachment) n += 1;
  return n;
}

export function TransactionsTab({
  period,
  initialAccountId,
}: {
  period: string;
  initialAccountId?: string;
}) {
  const navigate = useNavigate();
  const [modal, setModal] = useState<ModalKind>(null);
  const [editingTx, setEditingTx] = useState<Transaction | null>(null);
  const [editingTransfer, setEditingTransfer] = useState<Transfer | null>(null);
  const [filters, setFilters] = useState<Filters>(() => ({
    ...initialFilters(),
    accountId: initialAccountId ?? "",
  }));
  const [filtersOpen, setFiltersOpen] = useState(!!initialAccountId);
  // Paginación: 50 por defecto. Persiste el tamaño elegido en localStorage
  // entre sesiones — el usuario que prefiere ver 200 no quiere reconfigurarlo.
  const [pageSize, setPageSize] = useState<number>(() => {
    if (typeof window === "undefined") return 50;
    const v = parseInt(localStorage.getItem("axorial.tx.pageSize") ?? "", 10);
    return [25, 50, 100, 200].includes(v) ? v : 50;
  });
  const [page, setPage] = useState(1);
  // Filtro visual rápido por categoría de movimiento. NO afecta los totales
  // de las cards (que reflejan el filtro server-side), solo lo que se ve en
  // la lista. Click en una card lo activa/desactiva.
  type FeedKindFilter = "all" | "ingreso" | "gasto" | "transferencia";
  const [feedKind, setFeedKind] = useState<FeedKindFilter>("all");
  // Toggle para mostrar/ocultar el grupo de cards fiscales (ISR, IVA,
  // utilidad). Persiste en localStorage porque algunos meses el usuario
  // quiere foco en lo operativo, no en la postura fiscal.
  const [showFiscal, setShowFiscal] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem("axorial.tx.showFiscal") !== "0";
  });

  // Estado del exporter de paquete ZIP. Mientras está corriendo, el botón
  // muestra el progreso (n/total) en lugar del label.
  const [packageStatus, setPackageStatus] = useState<{
    stage: "excel" | "docs" | "zipping";
    processed: number;
    total: number;
  } | null>(null);
  // Toggle "expandir splits": cuando ON, cada tx con splits se renderiza
  // como sus N partes en lugar del padre agregado.
  // Default ON: el desglose por subitem es lo que la operadora necesita
  // ver para identificar a qué cubre cada parte (internet vs teléfono,
  // honorarios separados de gastos compartidos). El usuario lo puede
  // colapsar y la preferencia se guarda en localStorage.
  const [expandSplits, setExpandSplits] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = localStorage.getItem("axorial.tx.expandSplits");
    return stored === null ? true : stored === "1";
  });

  // Cuando cambia el periodo (por nav del header), reset al filtro
  // inicial (mes actual). Mantiene la convención de abrir siempre en el
  // contexto fiscal del mes en curso.
  useEffect(() => {
    setFilters(initialFilters());
    setFiltersOpen(false);
  }, [period]);

  // Reset de página cuando cambian filtros, pageSize o feedKind para no
  // quedar en una página fantasma fuera del rango disponible.
  const filterQs = buildQueryString(filters);
  useEffect(() => {
    setPage(1);
  }, [filterQs, pageSize, feedKind]);

  // Deep-link account=X: aplicar UNA vez y limpiar el URL para que la
  // próxima vez que se entre a la pestaña los filtros estén vacíos.
  // Sin esto el ?account=X persistía y el filtro de cuenta reaparecía
  // al volver a Transacciones desde otro tab.
  useEffect(() => {
    if (initialAccountId !== undefined && initialAccountId !== "") {
      setFilters((prev) => ({ ...prev, accountId: initialAccountId }));
      setFiltersOpen(true);
      navigate({
        to: "/",
        search: (prev: Record<string, unknown>) => {
          const next = { ...prev };
          delete next.account;
          return next;
        },
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeCount = activeFilterCount(filters, period);

  const txs = useQuery<{
    transactions: (Transaction & {
      companyName: string | null;
      attachmentFilename: string | null;
      attachmentSize: number | null;
      documents?: TxDocument[];
      splits?: TxSplit[];
    })[];
  }>({
    queryKey: ["finance", "transactions", filterQs],
    queryFn: () => api.get(`/finance/transactions?${filterQs}`),
  });

  const accounts = useQuery<{ accounts: Account[] }>({
    queryKey: ["finance", "accounts"],
    queryFn: () => api.get("/finance/accounts"),
  });

  const categories = useQuery<{ categories: Category[] }>({
    queryKey: ["finance", "categories"],
    queryFn: () => api.get("/finance/categories"),
  });

  const transfers = useQuery<{ transfers: Transfer[] }>({
    queryKey: ["finance", "transfers", filters.from, filters.to],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      const qs = params.toString();
      return api.get(`/finance/transfers${qs ? `?${qs}` : ""}`);
    },
  });

  const accountById = new Map(accounts.data?.accounts.map((a) => [a.id, a.name]) ?? []);
  const categoryById = new Map(categories.data?.categories.map((c) => [c.id, c.name]) ?? []);
  // Companies para resolver overrides de splits. Si el listado de tx ya
  // trae companyName del padre, esto solo se usa cuando un split apunta a
  // una empresa diferente.
  const companiesQuery = useQuery<{ companies: { id: string; name: string }[] }>({
    queryKey: ["companies"],
    queryFn: () => api.get("/companies"),
    staleTime: 5 * 60_000,
  });
  const companyNameById = new Map(companiesQuery.data?.companies.map((c) => [c.id, c.name]) ?? []);
  const txList = txs.data?.transactions ?? [];
  const transferList = transfers.data?.transfers ?? [];

  // Lista unificada ordenada por fecha desc, txs y transferencias intercaladas.
  // Decisión UX: si el usuario filtró por algo específico de tx (categoría,
  // payment status, etc.) ocultamos transferencias, ya que no aplican.
  const transfersApply =
    !filters.kind && !filters.categoryId && !filters.paymentStatus && !filters.invoiceStatus;
  type FeedItem =
    | { kind: "tx"; date: string; row: (typeof txList)[number] }
    | { kind: "transfer"; date: string; row: Transfer };
  const feed: FeedItem[] = [
    ...txList.map((t): FeedItem => ({ kind: "tx", date: t.date, row: t })),
    ...(transfersApply
      ? transferList.map((t): FeedItem => ({ kind: "transfer", date: t.date, row: t }))
      : []),
  ].toSorted((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  // Aplicar feedKind sobre el feed unificado antes de paginar.
  const displayFeed =
    feedKind === "all"
      ? feed
      : feed.filter((item) =>
          feedKind === "transferencia"
            ? item.kind === "transfer"
            : item.kind === "tx" && item.row.kind === feedKind,
        );

  const totalItems = displayFeed.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);
  const sliceStart = (currentPage - 1) * pageSize;
  const sliceEnd = sliceStart + pageSize;
  const pageFeed = displayFeed.slice(sliceStart, sliceEnd);

  // Totales del filtro actual (sobre todas las páginas, no solo la visible).
  // Convención: el monto grande es el NETO (subtotal sin IVA, dinero real
  // del negocio). El IVA va aparte porque es del SAT. El bruto se muestra
  // como suma "IVA $X = $bruto".
  const summary = {
    ingresoSubtotal: 0,
    ingresoIva: 0,
    ingresoCount: 0,
    gastoSubtotal: 0,
    gastoIva: 0,
    gastoCount: 0,
    transferTotal: 0,
    transferCount: 0,
  };
  for (const t of txList) {
    if (t.kind === "ingreso") {
      summary.ingresoSubtotal += t.subtotalCents;
      summary.ingresoIva += t.ivaCents;
      summary.ingresoCount += 1;
    } else if (t.kind === "gasto") {
      summary.gastoSubtotal += t.subtotalCents;
      summary.gastoIva += t.ivaCents;
      summary.gastoCount += 1;
    }
  }
  if (transfersApply) {
    for (const t of transferList) {
      summary.transferTotal += t.amountCents;
      summary.transferCount += 1;
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="input input-bordered input-sm flex items-center gap-2 min-w-0 w-64">
            <Search className="size-3.5 text-base-content/40 shrink-0" />
            <input
              type="text"
              className="grow min-w-0"
              placeholder="Buscar concepto o contraparte…"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            />
            {filters.q && (
              <button
                type="button"
                onClick={() => setFilters((f) => ({ ...f, q: "" }))}
                className="text-base-content/40 hover:text-base-content"
                aria-label="Limpiar"
              >
                <X className="size-3" />
              </button>
            )}
          </label>
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={clsx(
              "btn btn-sm",
              activeCount > 0 ? "btn-primary" : "btn-ghost border border-base-300/60",
            )}
          >
            <SlidersHorizontal className="size-4" />
            Filtros
            {activeCount > 0 && (
              <span className="badge badge-xs badge-neutral tabular-nums">{activeCount}</span>
            )}
          </button>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={() => setFilters(defaultFilters(period))}
              className="btn btn-ghost btn-sm text-base-content/65"
            >
              Limpiar todo
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label
            className="cursor-pointer flex items-center gap-2 px-2 py-1.5 rounded-md border border-base-300/60 bg-base-200/40 text-xs"
            title="Mostrar tarjetas de utilidad neta, ISR e IVA del filtro actual"
          >
            <input
              type="checkbox"
              className="toggle toggle-xs toggle-primary"
              checked={showFiscal}
              onChange={(e) => {
                setShowFiscal(e.target.checked);
                if (typeof window !== "undefined") {
                  localStorage.setItem("axorial.tx.showFiscal", e.target.checked ? "1" : "0");
                }
              }}
            />
            <span className="hidden sm:inline">Impuestos y utilidad</span>
          </label>
          <label
            className="cursor-pointer flex items-center gap-2 px-2 py-1.5 rounded-md border border-base-300/60 bg-base-200/40 text-xs"
            title="Mostrar cada tx como sus sub-cuentas en lugar del padre agregado"
          >
            <input
              type="checkbox"
              className="toggle toggle-xs toggle-primary"
              checked={expandSplits}
              onChange={(e) => {
                setExpandSplits(e.target.checked);
                if (typeof window !== "undefined") {
                  localStorage.setItem("axorial.tx.expandSplits", e.target.checked ? "1" : "0");
                }
              }}
            />
            <span className="hidden sm:inline">Expandir sub-cuentas</span>
          </label>
          <button
            type="button"
            onClick={() =>
              exportTransactionsToExcel({
                transactions: txList,
                period: filters.from?.slice(0, 7) ?? period,
                accountById,
                categoryById,
              })
            }
            disabled={txList.length === 0}
            className="btn btn-sm btn-outline btn-primary"
            title="Descargar Excel con resumen, transacciones y categorías del filtro actual"
          >
            <FileSpreadsheet className="size-4" /> Excel
          </button>
          <button
            type="button"
            onClick={async () => {
              if (packageStatus) return;
              try {
                await exportFinancePackageZip({
                  transactions: txList,
                  period: filters.from?.slice(0, 7) ?? period,
                  accountById,
                  categoryById,
                  onProgress: setPackageStatus,
                });
              } catch (e) {
                console.error("package export failed", e);
              } finally {
                setPackageStatus(null);
              }
            }}
            disabled={txList.length === 0 || !!packageStatus}
            className="btn btn-sm btn-outline btn-primary"
            title="Descargar ZIP con Excel + carpeta de documentos por transacción — listo para el contador"
          >
            {packageStatus ? (
              <>
                <span className="loading loading-spinner loading-xs" />
                <span className="hidden sm:inline tabular-nums">
                  {packageStatus.stage === "excel"
                    ? "Excel…"
                    : packageStatus.stage === "docs"
                      ? `Docs ${packageStatus.processed}/${packageStatus.total}`
                      : "Empaquetando…"}
                </span>
              </>
            ) : (
              <>
                <Package className="size-4" /> Paquete
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setModal("income")}
            className="btn btn-sm btn-outline btn-success"
          >
            <TrendingUp className="size-4" /> Ingreso
          </button>
          <button
            type="button"
            onClick={() => setModal("expense")}
            className="btn btn-sm btn-outline btn-error"
          >
            <TrendingDown className="size-4" /> Gasto
          </button>
          <button
            type="button"
            onClick={() => setModal("transfer")}
            className="btn btn-sm btn-outline btn-info"
          >
            <ArrowLeftRight className="size-4" /> Transferencia
          </button>
        </div>
      </div>

      {filtersOpen && (
        <FiltersPanel
          filters={filters}
          onChange={setFilters}
          accounts={accounts.data?.accounts ?? []}
          categories={categories.data?.categories ?? []}
        />
      )}

      <SummaryCards
        summary={summary}
        showTransfers={transfersApply}
        activeKind={feedKind}
        onToggleKind={(k) => setFeedKind((prev) => (prev === k ? "all" : k))}
      />

      <PeriodBalancesStrip
        from={filters.from}
        to={filters.to}
        accountId={filters.accountId || undefined}
      />

      {showFiscal &&
        (() => {
          const baseIsr = summary.ingresoSubtotal - summary.gastoSubtotal;
          const isr = Math.max(0, Math.round(baseIsr * ISR_RATE_RESICO_PM));
          // Utilidad neta del negocio = margen − ISR. El IVA no aparece
          // aquí porque es del SAT (no es dinero del negocio).
          const utilidad = baseIsr - isr;
          const ivaNeto = summary.ingresoIva - summary.gastoIva;
          return <FiscalCards isrCents={isr} ivaNetoCents={ivaNeto} utilidadNetaCents={utilidad} />;
        })()}

      <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-base-content/55">
        <div>
          {txList.length} transacciones
          {transfersApply && ` · ${transferList.length} transferencias`}
          {activeCount > 0 && " · filtros aplicados"}
          {totalItems > pageSize && (
            <>
              {" · "}
              <span className="tabular-nums">
                {sliceStart + 1}–{Math.min(sliceEnd, totalItems)} de {totalItems}
              </span>
            </>
          )}
        </div>
        <PageSizeSelect
          value={pageSize}
          onChange={(n) => {
            setPageSize(n);
            if (typeof window !== "undefined") {
              localStorage.setItem("axorial.tx.pageSize", String(n));
            }
          }}
        />
      </div>

      {txs.isLoading || accounts.isLoading ? (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : feed.length === 0 ? (
        <EmptyState onAdd={() => setModal("expense")} />
      ) : (
        <>
          <ul className="card-elevated overflow-hidden divide-y-0">
            {pageFeed.flatMap((item) => {
              if (item.kind === "transfer") {
                return [
                  <TransferRow
                    key={`tr-${item.row.id}`}
                    transfer={item.row}
                    fromName={accountById.get(item.row.fromAccountId)}
                    toName={accountById.get(item.row.toAccountId)}
                    onEdit={() => setEditingTransfer(item.row)}
                  />,
                ];
              }
              const tx = item.row;
              const splits = (tx.splits ?? []) as TxSplit[];
              const showSplits = expandSplits && splits.length > 0;
              const parentRow = (
                <TransactionRow
                  key={`tx-${tx.id}`}
                  tx={tx}
                  accountName={accountById.get(tx.accountId)}
                  categoryName={tx.categoryId ? categoryById.get(tx.categoryId) : undefined}
                  categoryNameById={categoryById}
                  companyNameById={companyNameById}
                  onEdit={() => setEditingTx(tx)}
                />
              );
              if (!showSplits) return [parentRow];
              const splitRows = splits.map((s) => (
                <TransactionRow
                  key={`split-${s.id}`}
                  tx={tx}
                  accountName={accountById.get(tx.accountId)}
                  categoryName={tx.categoryId ? categoryById.get(tx.categoryId) : undefined}
                  categoryNameById={categoryById}
                  companyNameById={companyNameById}
                  splitAs={s}
                  onEdit={() => setEditingTx(tx)}
                />
              ));
              return [parentRow, ...splitRows];
            })}
          </ul>
          {totalPages > 1 && (
            <PaginationBar page={currentPage} totalPages={totalPages} onChange={setPage} />
          )}
        </>
      )}

      {modal === "income" && <IncomeDialog onClose={() => setModal(null)} />}
      {modal === "expense" && <ExpenseDialog onClose={() => setModal(null)} />}
      {modal === "transfer" && <TransferDialog onClose={() => setModal(null)} />}

      {editingTx && editingTx.kind === "ingreso" && (
        <IncomeDialog onClose={() => setEditingTx(null)} existingTx={editingTx} />
      )}
      {editingTx && editingTx.kind === "gasto" && (
        <ExpenseDialog onClose={() => setEditingTx(null)} existingTx={editingTx} />
      )}
      {editingTransfer && (
        <TransferDialog
          onClose={() => setEditingTransfer(null)}
          existingTransfer={editingTransfer}
        />
      )}
    </div>
  );
}

function FiltersPanel({
  filters,
  onChange,
  accounts,
  categories,
}: {
  filters: Filters;
  onChange: (next: Filters) => void;
  accounts: Account[];
  categories: Category[];
}) {
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    onChange({ ...filters, [key]: value });

  const active = activeShortcut(filters);
  const shortcutClass = (k: ShortcutKind) =>
    clsx("btn btn-xs", active === k ? "btn-primary" : "btn-ghost");

  // Atajos corte-a-corte: solo cuando la cuenta filtrada es una TDC con
  // día de corte configurado (ej. Konfio corta el 17).
  const cutAccount = filters.accountId
    ? accounts.find((a) => a.id === filters.accountId && a.type === "credit_card" && a.cutDay != null)
    : undefined;
  const cutShortcutClass = (offset: number) => {
    if (!cutAccount?.cutDay) return "btn btn-xs btn-ghost";
    const r = cutRange(cutAccount.cutDay, offset);
    const isActive = filters.from === r.from && filters.to === r.to;
    return clsx("btn btn-xs", isActive ? "btn-primary" : "btn-ghost");
  };

  return (
    <div className="card-elevated p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2 -mt-1">
        <span className="text-xs text-base-content/55 mr-1">Atajos:</span>
        <button
          type="button"
          onClick={() => onChange({ ...filters, ...rangeShortcut("week") })}
          className={shortcutClass("week")}
        >
          Esta semana
        </button>
        <button
          type="button"
          onClick={() => onChange({ ...filters, ...rangeShortcut("month") })}
          className={shortcutClass("month")}
        >
          Mes actual
        </button>
        <button
          type="button"
          onClick={() => onChange({ ...filters, ...rangeShortcut("lastMonth") })}
          className={shortcutClass("lastMonth")}
        >
          Mes anterior
        </button>
        <button
          type="button"
          onClick={() => onChange({ ...filters, ...rangeShortcut("quarter") })}
          className={shortcutClass("quarter")}
        >
          Este trimestre
        </button>
        <button
          type="button"
          onClick={() => onChange({ ...filters, ...rangeShortcut("ytd") })}
          className={shortcutClass("ytd")}
        >
          Lo que va del año
        </button>
        {cutAccount?.cutDay != null && (
          <>
            <button
              type="button"
              onClick={() => onChange({ ...filters, ...cutRange(cutAccount.cutDay!, 0) })}
              className={cutShortcutClass(0)}
              title={`Periodo en curso de ${cutAccount.name} (corta el día ${cutAccount.cutDay})`}
            >
              Corte actual
            </button>
            <button
              type="button"
              onClick={() => onChange({ ...filters, ...cutRange(cutAccount.cutDay!, -1) })}
              className={cutShortcutClass(-1)}
              title={`Último periodo cerrado de ${cutAccount.name} (corta el día ${cutAccount.cutDay})`}
            >
              Corte anterior
            </button>
          </>
        )}
        <button
          type="button"
          onClick={() => onChange({ ...filters, from: "", to: "" })}
          className={shortcutClass("none")}
        >
          Sin restricción de fecha
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <FilterField label="Desde" active={!!filters.from}>
          <input
            type="date"
            className="input input-bordered input-sm w-full"
            value={filters.from}
            onChange={(e) => set("from", e.target.value)}
          />
        </FilterField>
        <FilterField label="Hasta" active={!!filters.to}>
          <input
            type="date"
            className="input input-bordered input-sm w-full"
            value={filters.to}
            onChange={(e) => set("to", e.target.value)}
          />
        </FilterField>
        <FilterField label="Tipo" active={!!filters.kind}>
          <select
            className="select select-bordered select-sm w-full"
            value={filters.kind}
            onChange={(e) => set("kind", e.target.value as Filters["kind"])}
          >
            <option value="">Todos</option>
            <option value="ingreso">Ingreso</option>
            <option value="gasto">Gasto</option>
          </select>
        </FilterField>
        <FilterField label="Cuenta" active={!!filters.accountId}>
          <select
            className="select select-bordered select-sm w-full"
            value={filters.accountId}
            onChange={(e) => set("accountId", e.target.value)}
          >
            <option value="">Todas</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Categoría" active={!!filters.categoryId}>
          <select
            className="select select-bordered select-sm w-full"
            value={filters.categoryId}
            onChange={(e) => set("categoryId", e.target.value)}
          >
            <option value="">Todas</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Status pago" active={!!filters.paymentStatus}>
          <select
            className="select select-bordered select-sm w-full"
            value={filters.paymentStatus}
            onChange={(e) => set("paymentStatus", e.target.value as Filters["paymentStatus"])}
          >
            <option value="">Todos</option>
            <option value="cobrado">Cobrado</option>
            <option value="pagado">Pagado</option>
            <option value="pendiente">Pendiente</option>
          </select>
        </FilterField>
        <FilterField label="Status factura" active={!!filters.invoiceStatus}>
          <select
            className="select select-bordered select-sm w-full"
            value={filters.invoiceStatus}
            onChange={(e) => set("invoiceStatus", e.target.value as Filters["invoiceStatus"])}
          >
            <option value="">Todos</option>
            <option value="facturado">Facturado</option>
            <option value="deducible_sin_factura">Deducible sin factura</option>
            <option value="pendiente">Pendiente</option>
            <option value="no_aplica">No aplica</option>
            <option value="olvidado">Olvidada (no facturada a tiempo)</option>
          </select>
        </FilterField>
        <FilterField label="Adjunto" active={!!filters.hasAttachment}>
          <select
            className="select select-bordered select-sm w-full"
            value={filters.hasAttachment}
            onChange={(e) => set("hasAttachment", e.target.value as Filters["hasAttachment"])}
          >
            <option value="">Todas</option>
            <option value="1">Con documento</option>
            <option value="0">Sin documento</option>
          </select>
        </FilterField>
        <FilterField label="Afecta totales" active={!!filters.affectsTotals}>
          <select
            className="select select-bordered select-sm w-full"
            value={filters.affectsTotals}
            onChange={(e) => set("affectsTotals", e.target.value as Filters["affectsTotals"])}
          >
            <option value="">Todas</option>
            <option value="1">Solo vivas</option>
            <option value="0">Solo históricas</option>
          </select>
        </FilterField>
      </div>
    </div>
  );
}

function FilterField({
  label,
  active,
  children,
}: {
  label: string;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span
        className={clsx(
          "text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5",
          active ? "text-primary" : "text-base-content/55",
        )}
      >
        {label}
        {active && (
          <span
            aria-hidden
            className="inline-block size-1.5 rounded-full bg-primary shadow-[0_0_0_2px_var(--color-primary)/15]"
          />
        )}
      </span>
      <div className={clsx("mt-1 rounded-md", active && "ring-2 ring-primary/30 ring-offset-1")}>
        {children}
      </div>
    </label>
  );
}

/**
 * Cards de resumen con los totales del filtro actual (suman TODA la página
 * de resultados, no solo lo visible). IVA se muestra como sub-monto en gris
 * porque es del SAT — no es ingreso/gasto propio del negocio.
 */
function SummaryCards({
  summary,
  showTransfers,
  activeKind,
  onToggleKind,
}: {
  summary: {
    ingresoSubtotal: number;
    ingresoIva: number;
    ingresoCount: number;
    gastoSubtotal: number;
    gastoIva: number;
    gastoCount: number;
    transferTotal: number;
    transferCount: number;
  };
  showTransfers: boolean;
  activeKind: "all" | "ingreso" | "gasto" | "transferencia";
  onToggleKind: (kind: "ingreso" | "gasto" | "transferencia") => void;
}) {
  return (
    <div
      className={clsx(
        "grid gap-3",
        showTransfers ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-1 sm:grid-cols-2",
      )}
    >
      <SummaryCard
        label="Ingresos"
        count={summary.ingresoCount}
        netCents={summary.ingresoSubtotal}
        ivaCents={summary.ingresoIva}
        toneClass="text-success"
        bgClass="bg-success/8"
        ringClass="ring-success/60"
        sign="+"
        Icon={TrendingUp}
        active={activeKind === "ingreso"}
        onClick={() => onToggleKind("ingreso")}
      />
      <SummaryCard
        label="Gastos"
        count={summary.gastoCount}
        netCents={summary.gastoSubtotal}
        ivaCents={summary.gastoIva}
        toneClass="text-error"
        bgClass="bg-error/8"
        ringClass="ring-error/60"
        sign="−"
        Icon={TrendingDown}
        active={activeKind === "gasto"}
        onClick={() => onToggleKind("gasto")}
      />
      {showTransfers && (
        <SummaryCard
          label="Transferencias"
          count={summary.transferCount}
          netCents={summary.transferTotal}
          toneClass="text-info"
          bgClass="bg-info/8"
          ringClass="ring-info/60"
          Icon={ArrowLeftRight}
          active={activeKind === "transferencia"}
          onClick={() => onToggleKind("transferencia")}
        />
      )}
    </div>
  );
}

function SummaryCard({
  label,
  count,
  netCents,
  ivaCents,
  toneClass,
  bgClass,
  ringClass,
  sign,
  Icon,
  active,
  onClick,
}: {
  label: string;
  count: number;
  netCents: number;
  ivaCents?: number;
  toneClass: string;
  bgClass: string;
  ringClass: string;
  sign?: "+" | "−";
  Icon: typeof TrendingUp;
  active: boolean;
  onClick: () => void;
}) {
  const hasIva = ivaCents !== undefined && ivaCents > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={active ? `Mostrar todo` : `Solo ${label.toLowerCase()}`}
      className={clsx(
        "card-elevated p-3 flex items-center gap-3 text-left transition-all",
        "hover:bg-base-200/40 cursor-pointer",
        active && `ring-2 ${ringClass}`,
      )}
    >
      <div className={clsx("size-10 rounded-full grid place-items-center shrink-0", bgClass)}>
        <Icon className={clsx("size-5", toneClass)} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-base-content/55 font-semibold flex items-center gap-1.5">
          {label}
          <span className="badge badge-xs badge-ghost tabular-nums font-normal">{count}</span>
        </div>
        <div className={clsx("font-display font-bold text-lg tabular-nums", toneClass)}>
          {sign}
          {formatMxnCents(netCents)}
        </div>
        {hasIva && (
          <div className="text-[11px] text-base-content/50 tabular-nums">
            IVA {formatMxnCents(ivaCents)} = {formatMxnCents(netCents + ivaCents)}
          </div>
        )}
      </div>
    </button>
  );
}

/**
 * Tarjetas fiscales calculadas desde el filtro actual. NO son clickeables:
 * son derivadas (sumas/diferencias) y mostrarlas filtradas no aporta. El
 * cálculo replica la lógica del backend para reflejar al instante el efecto
 * de cualquier filtro/ajuste local.
 */
function FiscalCards({
  isrCents,
  ivaNetoCents,
  utilidadNetaCents,
}: {
  isrCents: number;
  ivaNetoCents: number;
  utilidadNetaCents: number;
}) {
  const utilidadTone = utilidadNetaCents >= 0 ? "text-success" : "text-error";
  const utilidadBg = utilidadNetaCents >= 0 ? "bg-success/8" : "bg-error/8";
  const ivaTone = ivaNetoCents > 0 ? "text-warning" : "text-success";
  const ivaBg = ivaNetoCents > 0 ? "bg-warning/8" : "bg-success/8";
  const ivaHint =
    ivaNetoCents > 0
      ? "A pagar al SAT"
      : ivaNetoCents < 0
        ? "A favor (compensable)"
        : "Equilibrado";
  return (
    <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
      <FiscalCard
        label="ISR del periodo"
        valueCents={isrCents}
        toneClass={isrCents > 0 ? "text-warning" : "text-base-content/70"}
        bgClass={isrCents > 0 ? "bg-warning/8" : "bg-base-200"}
        Icon={Calculator}
        hint="30% × max(0, utilidad neta)"
      />
      <FiscalCard
        label="IVA pendiente"
        valueCents={Math.abs(ivaNetoCents)}
        sign={ivaNetoCents > 0 ? undefined : ivaNetoCents < 0 ? "−" : undefined}
        toneClass={ivaTone}
        bgClass={ivaBg}
        Icon={Percent}
        hint={ivaHint}
      />
      <FiscalCard
        label="Utilidad neta"
        valueCents={utilidadNetaCents}
        sign={utilidadNetaCents >= 0 ? "+" : "−"}
        toneClass={utilidadTone}
        bgClass={utilidadBg}
        Icon={Wallet}
        hint="Ingresos − gastos − ISR"
      />
    </div>
  );
}

function FiscalCard({
  label,
  valueCents,
  toneClass,
  bgClass,
  sign,
  Icon,
  hint,
}: {
  label: string;
  valueCents: number;
  toneClass: string;
  bgClass: string;
  sign?: "+" | "−";
  Icon: typeof TrendingUp;
  hint?: string;
}) {
  return (
    <div className="card-elevated p-3 flex items-center gap-3">
      <div className={clsx("size-10 rounded-full grid place-items-center shrink-0", bgClass)}>
        <Icon className={clsx("size-5", toneClass)} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-base-content/55 font-semibold">
          {label}
        </div>
        <div className={clsx("font-display font-bold text-lg tabular-nums", toneClass)}>
          {sign}
          {formatMxnCents(valueCents)}
        </div>
        {hint && <div className="text-[11px] text-base-content/50 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function PageSizeSelect({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <label className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
      <span>Por página</span>
      <select
        className="select select-bordered select-xs w-20"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        <option value={25}>25</option>
        <option value={50}>50</option>
        <option value={100}>100</option>
        <option value={200}>200</option>
      </select>
    </label>
  );
}

function PaginationBar({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  const canPrev = page > 1;
  const canNext = page < totalPages;
  return (
    <div className="flex items-center justify-center gap-1 pt-2">
      <button
        type="button"
        onClick={() => onChange(1)}
        disabled={!canPrev}
        className="btn btn-ghost btn-xs btn-square"
        aria-label="Primera página"
        title="Primera página"
      >
        <ChevronsLeft className="size-4" />
      </button>
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={!canPrev}
        className="btn btn-ghost btn-xs btn-square"
        aria-label="Anterior"
        title="Anterior"
      >
        <ChevronLeft className="size-4" />
      </button>
      <span className="text-xs text-base-content/70 tabular-nums px-2">
        Página {page} de {totalPages}
      </span>
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={!canNext}
        className="btn btn-ghost btn-xs btn-square"
        aria-label="Siguiente"
        title="Siguiente"
      >
        <ChevronRight className="size-4" />
      </button>
      <button
        type="button"
        onClick={() => onChange(totalPages)}
        disabled={!canNext}
        className="btn btn-ghost btn-xs btn-square"
        aria-label="Última página"
        title="Última página"
      >
        <ChevronsRight className="size-4" />
      </button>
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="card-elevated py-16 text-center">
      <div className="size-14 rounded-full bg-primary/10 grid place-items-center mx-auto mb-4">
        <Receipt className="size-6 text-primary" />
      </div>
      <h3 className="font-display font-semibold text-base-content mb-1">
        Sin transacciones este mes
      </h3>
      <p className="text-sm text-base-content/60 mb-5">
        Empieza registrando ingresos y gastos del mes.
      </p>
      <button type="button" onClick={onAdd} className="btn btn-primary">
        Agregar primer gasto
      </button>
    </div>
  );
}

/**
 * Etiquetas y tonos para invoice_status en la fila. `null` para los casos
 * donde no aporta info (ej. tx pendiente que ya está marcada por payment).
 */
const INVOICE_STATUS_META: Record<
  "facturado" | "deducible_sin_factura" | "pendiente" | "no_aplica" | "olvidado",
  { label: string; tone: string; help: string }
> = {
  facturado: {
    label: "facturada",
    tone: "text-info",
    help: "Con CFDI · deducible para ISR + IVA acreditable",
  },
  deducible_sin_factura: {
    label: "sin CFDI",
    tone: "text-warning",
    help: "Pago al extranjero o recibo no fiscal · deducible para ISR pero NO suma IVA acreditable",
  },
  pendiente: {
    label: "factura pendiente",
    tone: "text-warning",
    help: "El gasto cuenta para ISR · falta CFDI para acreditar IVA",
  },
  no_aplica: {
    label: "no deducible",
    tone: "text-error",
    help: "Gasto no deducible (personal, multa, donativo no aplicable)",
  },
  olvidado: {
    label: "olvidada",
    tone: "text-error",
    help: "Se nos olvidó facturar · ya pasó la ventana SAT y no se puede emitir CFDI. No deducible (deducción perdida).",
  },
};

interface TxSplit {
  id: string;
  categoryId: string | null;
  projectId: string | null;
  companyId: string | null;
  description: string | null;
  subtotalCents: number;
  ivaCents: number;
  totalCents: number;
}

function TransactionRow({
  tx,
  accountName,
  categoryName,
  categoryNameById,
  companyNameById,
  splitAs,
  onEdit,
}: {
  tx: Transaction & {
    companyName?: string | null;
    attachmentFilename?: string | null;
    attachmentSize?: number | null;
    documents?: TxDocument[];
    splits?: TxSplit[];
    /** Saldo de la cuenta DESPUÉS de aplicar esta tx (lo computa fin-api).
     *  null para cuentas borradas o cuando el back no lo resolvió. */
    balanceAfterCents?: number | null;
  };
  accountName?: string;
  categoryName?: string;
  /** Maps para resolver overrides del split (categoría/empresa). */
  categoryNameById?: Map<string, string>;
  companyNameById?: Map<string, string>;
  /** Cuando se pasa, la fila representa UN split del padre (no el padre completo). */
  splitAs?: TxSplit;
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const docCount = tx.documents?.length ?? (tx.finDocumentId ? 1 : 0);
  const splitCount = tx.splits?.length ?? 0;
  const remove = useMutation({
    mutationFn: () => api.del(`/finance/transactions/${tx.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finance"] }),
  });
  // Flip ingreso ↔ gasto en un click. Útil para corregir filas N06 PAGO
  // CUENTA DE TERCERO de BBVA y otros casos ambiguos donde el parser
  // adivinó la dirección. Actualiza paymentStatus para mantener
  // consistencia (cobrado↔pagado).
  const flipKind = useMutation({
    mutationFn: () => {
      const newKind = tx.kind === "ingreso" ? "gasto" : "ingreso";
      const newPaymentStatus =
        tx.paymentStatus === "cobrado" || tx.paymentStatus === "pagado"
          ? newKind === "ingreso"
            ? "cobrado"
            : "pagado"
          : tx.paymentStatus;
      return api.patch(`/finance/transactions/${tx.id}`, {
        kind: newKind,
        paymentStatus: newPaymentStatus,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finance"] }),
  });

  const isIngreso = tx.kind === "ingreso";
  const sign = isIngreso ? "+" : "−";
  const colorClass = isIngreso ? "text-success" : "text-error";
  const hasAttachment = docCount > 0;

  // Título: explícito del usuario > descripción > contraparte > empresa.
  // En modo split, el título es la descripción del split (cae al del padre).
  const title = splitAs
    ? splitAs.description?.trim() ||
      tx.title?.trim() ||
      tx.counterparty?.trim() ||
      "Parte sin descripción"
    : tx.title?.trim() ||
      tx.description?.trim() ||
      tx.counterparty?.trim() ||
      tx.companyName?.trim() ||
      "Sin descripción";
  const isPaid = tx.paymentStatus === "cobrado" || tx.paymentStatus === "pagado";
  const invoiceMeta = INVOICE_STATUS_META[tx.invoiceStatus] ?? null;
  const subCandidate = (tx.description ?? tx.counterparty ?? "").trim();
  const subDescription =
    !splitAs && subCandidate && subCandidate !== title.trim() ? subCandidate : null;

  // En modo split: chips, montos y categoría vienen del split (con fallback
  // al padre si el split no override eso).
  const effectiveCompanyName = splitAs
    ? splitAs.companyId
      ? (companyNameById?.get(splitAs.companyId) ?? tx.companyName ?? null)
      : (tx.companyName ?? null)
    : (tx.companyName ?? null);
  const effectiveCategoryName = splitAs
    ? splitAs.categoryId
      ? (categoryNameById?.get(splitAs.categoryId) ?? categoryName)
      : categoryName
    : categoryName;
  const displaySubtotal = splitAs?.subtotalCents ?? tx.subtotalCents;
  const displayIva = splitAs?.ivaCents ?? tx.ivaCents;
  const displayTotal = splitAs?.totalCents ?? tx.totalCents;

  return (
    <li
      className={clsx(
        "group bg-base-100 hover:bg-base-200/40 transition-colors",
        "border-b border-base-300/40 last:border-b-0",
        "px-3 py-2.5 flex items-center gap-3",
        splitAs && "pl-10 bg-base-200/20",
      )}
    >
      {/* Icon avatar con dirección del flujo */}
      <div
        className={clsx(
          "size-9 rounded-full grid place-items-center shrink-0",
          isIngreso ? "bg-success/10 text-success" : "bg-error/10 text-error",
          splitAs && "size-7 opacity-70",
        )}
        aria-hidden
      >
        {isIngreso ? <ArrowDownLeft className="size-4" /> : <ArrowUpRight className="size-4" />}
      </div>

      {/* Título + metadata (cuenta · fecha · tags) + descripción del banco */}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{title}</span>
          {!splitAs && splitCount > 0 && (
            <span
              className="badge badge-xs badge-primary badge-outline shrink-0 text-[10px] font-normal gap-1"
              title={`Desglosado en ${splitCount} sub-cuentas`}
            >
              <GitBranch className="size-2.5" />
              {splitCount} sub
            </span>
          )}
          {effectiveCompanyName && (
            <span
              className="badge badge-xs badge-ghost shrink-0 text-[10px] font-normal gap-1"
              title={isIngreso ? "Cliente" : "Proveedor"}
            >
              <Building2 className="size-2.5" />
              {effectiveCompanyName}
            </span>
          )}
          {effectiveCategoryName && (
            <span className="badge badge-xs badge-outline shrink-0 text-[10px] font-normal">
              {effectiveCategoryName}
            </span>
          )}
          {tx.isAsimilado && !splitAs && (
            <span
              className="badge badge-xs badge-warning shrink-0 text-[10px] font-normal gap-1"
              title="Asimilado a salarios (Art. 96 LISR)"
            >
              <UserCheck className="size-2.5" />
              Asimilado
            </span>
          )}
        </div>
        <div className="text-xs text-base-content/55 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span className="truncate max-w-[10rem]">{accountName ?? "—"}</span>
          <span className="text-base-content/30">•</span>
          <span className="tabular-nums">{formatDate(tx.date)}</span>
          {!isPaid && (
            <>
              <span className="text-base-content/30">•</span>
              <span className="text-warning text-[11px] font-medium">{tx.paymentStatus}</span>
            </>
          )}
          {invoiceMeta && (
            <>
              <span className="text-base-content/30">•</span>
              <span
                className={clsx("text-[11px] font-medium", invoiceMeta.tone)}
                title={invoiceMeta.help}
              >
                {invoiceMeta.label}
              </span>
            </>
          )}
          {!tx.affectsTotals && (
            <>
              <span className="text-base-content/30">•</span>
              <span className="text-base-content/50 text-[11px]">histórico</span>
            </>
          )}
        </div>
        {subDescription && (
          <div className="text-[11px] text-base-content/45 mt-0.5 truncate" title={subDescription}>
            {subDescription}
          </div>
        )}
      </div>

      {/* Monto: grande = NETO (subtotal sin IVA). Debajo: "IVA $X = $bruto"
          si la tx tiene IVA, y saldo acumulado de la cuenta después de
          esta tx (ayuda a auditar cronológicamente). Ancho fijo para
          alinear con TransferRow. */}
      <div className="text-right shrink-0 w-40">
        <div className={clsx("text-base font-bold tabular-nums", colorClass)}>
          {sign}
          {formatMxnCents(displaySubtotal)}
        </div>
        {displayIva > 0 && (
          <div className="text-[11px] text-base-content/50 tabular-nums">
            IVA {formatMxnCents(displayIva)} = {formatMxnCents(displayTotal)}
          </div>
        )}
        {tx.balanceAfterCents != null && !splitAs && (
          <div
            className="text-[11px] text-base-content/55 tabular-nums"
            title="Saldo de la cuenta tras esta transacción"
          >
            Saldo {formatMxnCents(tx.balanceAfterCents)}
          </div>
        )}
      </div>

      {/* Acciones — 3 slots fijos para alinear con TransferRow. En modo
          split, las acciones aplican al padre, así que mostramos solo el
          lápiz (que abre el padre) y dejamos los otros slots vacíos. */}
      <div className="grid grid-cols-3 gap-0.5 shrink-0 w-20 place-items-center">
        {splitAs ? (
          <span aria-hidden className="btn btn-ghost btn-xs btn-square invisible" />
        ) : tx.documentedExternally && !hasAttachment ? (
          <button
            type="button"
            onClick={() => setAttachmentOpen(true)}
            className="btn btn-ghost btn-xs btn-square text-info"
            title="Documento declarado fuera del sistema — no hace falta subirlo"
            aria-label="Documento externo"
          >
            <CheckCircle2 className="size-3.5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setAttachmentOpen(true)}
            className={clsx(
              "btn btn-ghost btn-xs btn-square transition-colors relative",
              hasAttachment ? "text-success" : "text-base-content/35 hover:text-primary",
            )}
            title={
              hasAttachment
                ? `${docCount} adjunto${docCount === 1 ? "" : "s"}`
                : "Adjuntar documento"
            }
            aria-label="Adjuntos"
          >
            <Paperclip className="size-3.5" />
            {docCount > 1 && (
              <span className="absolute -top-1 -right-1 text-[9px] font-bold bg-success text-success-content rounded-full size-3.5 grid place-items-center tabular-nums">
                {docCount}
              </span>
            )}
          </button>
        )}
        <button
          type="button"
          onClick={onEdit}
          className="btn btn-ghost btn-xs btn-square text-base-content/60 hover:text-primary transition-colors"
          aria-label="Editar"
          title={splitAs ? "Editar padre" : "Editar"}
        >
          <Pencil className="size-3.5" />
        </button>
        {splitAs ? (
          <span aria-hidden className="btn btn-ghost btn-xs btn-square invisible" />
        ) : (
          <div className="dropdown dropdown-end">
            <button
              tabIndex={0}
              type="button"
              className="btn btn-ghost btn-xs btn-square"
              aria-label="Más acciones"
              title="Más acciones"
            >
              <MoreHorizontal className="size-3.5" />
            </button>
            <ul
              tabIndex={0}
              className="dropdown-content menu menu-xs bg-base-100 rounded-box z-10 w-52 p-1 shadow-lg border border-base-300/60"
            >
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setConvertOpen(true);
                    (document.activeElement as HTMLElement | null)?.blur();
                  }}
                  className="text-info flex gap-2"
                >
                  <GitBranch className="size-3.5" /> Conciliar como transferencia
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Cambiar de ${tx.kind} a ${isIngreso ? "gasto" : "ingreso"}?`)) {
                      flipKind.mutate();
                    }
                    (document.activeElement as HTMLElement | null)?.blur();
                  }}
                  disabled={flipKind.isPending}
                  className="text-warning flex gap-2"
                >
                  <ArrowLeftRight className="size-3.5" /> Cambiar a{" "}
                  {isIngreso ? "gasto" : "ingreso"}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm("¿Eliminar transacción?")) remove.mutate();
                    (document.activeElement as HTMLElement | null)?.blur();
                  }}
                  disabled={remove.isPending}
                  className="text-error flex gap-2"
                >
                  <Trash2 className="size-3.5" /> Eliminar
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {attachmentOpen && (
        <AttachmentManagerDialog tx={tx} onClose={() => setAttachmentOpen(false)} />
      )}
      {convertOpen && <ConvertToTransferDialog tx={tx} onClose={() => setConvertOpen(false)} />}
    </li>
  );
}

/**
 * Modal para convertir manualmente una transacción a transferencia entre
 * cuentas propias. Útil cuando la reconciliación automática del bulk
 * import no detectó el match (ej. desfase de fechas mayor a la ventana).
 */
type TransferCandidate = Transaction & { accountName: string; dayDiff: number };

function ConvertToTransferDialog({ tx, onClose }: { tx: Transaction; onClose: () => void }) {
  const qc = useQueryClient();
  const [otherAccountId, setOtherAccountId] = useState("");
  const [selectedTxId, setSelectedTxId] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState(false);

  const accounts = useQuery<{ accounts: Account[] }>({
    queryKey: ["finance", "accounts"],
    queryFn: () => api.get("/finance/accounts"),
  });
  const candidatesQuery = useQuery<{ candidates: TransferCandidate[] }>({
    queryKey: ["finance", "transfer-candidates", tx.id],
    queryFn: () => api.get(`/finance/transactions/${tx.id}/transfer-candidates`),
  });

  const convert = useMutation({
    mutationFn: (body: { otherAccountId: string; otherTxId?: string }) =>
      api.post(`/finance/transactions/${tx.id}/convert-to-transfer`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance"] });
      onClose();
    },
  });

  const candidates = candidatesQuery.data?.candidates ?? [];
  const otherAccounts = (accounts.data?.accounts ?? []).filter((a) => a.id !== tx.accountId);
  const direction = tx.kind === "ingreso" ? "entró" : "salió";

  const selectCandidate = (cand: TransferCandidate) => {
    convert.mutate({ otherAccountId: cand.accountId, otherTxId: cand.id });
    setSelectedTxId(cand.id);
  };

  return (
    <div className="modal modal-open" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-box max-w-2xl">
        <h3 className="font-display font-semibold text-lg mb-2">Conciliar como transferencia</h3>
        <p className="text-sm text-base-content/65 mb-3">
          Esta tx ({formatMxnCents(tx.totalCents)}) {direction}{" "}
          {tx.kind === "ingreso" ? "a esta cuenta" : "de esta cuenta"}. Si su pareja ya está en la
          lista, selecciónala abajo. Ambas se vincularán como una sola transferencia entre cuentas
          propias.
        </p>

        {!manualMode && (
          <div className="border border-base-300 rounded-lg overflow-hidden">
            <div className="bg-base-200/50 px-3 py-1.5 text-xs font-medium text-base-content/70">
              Posibles parejas ({candidates.length}) — mismo monto, cuenta distinta, ±14 días
            </div>
            {candidatesQuery.isLoading ? (
              <div className="p-6 text-center text-sm text-base-content/50">
                <span className="loading loading-spinner loading-sm" /> Buscando...
              </div>
            ) : candidates.length === 0 ? (
              <div className="p-4 text-center text-sm text-base-content/55">
                No encontramos candidatos automáticos. Usa el modo manual abajo.
              </div>
            ) : (
              <ul className="divide-y divide-base-300/60 max-h-64 overflow-y-auto">
                {candidates.map((c) => (
                  <li
                    key={c.id}
                    className={clsx(
                      "px-3 py-2 flex items-center gap-3 hover:bg-base-200/40 cursor-pointer transition-colors",
                      selectedTxId === c.id && "bg-info/10",
                    )}
                    onClick={() => selectCandidate(c)}
                  >
                    <div
                      className={clsx(
                        "size-2 rounded-full shrink-0",
                        c.kind === "ingreso" ? "bg-success" : "bg-error",
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {c.counterparty || c.description || "(sin descripción)"}
                      </div>
                      <div className="text-xs text-base-content/55 truncate">
                        {c.accountName} · {formatDate(c.date)}
                        {c.dayDiff !== 0 && (
                          <span className="ml-1">
                            ({c.dayDiff > 0 ? "+" : ""}
                            {c.dayDiff}d)
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={clsx(
                        "text-sm font-semibold tabular-nums shrink-0",
                        c.kind === "ingreso" ? "text-success" : "text-error",
                      )}
                    >
                      {c.kind === "ingreso" ? "+" : "−"}
                      {formatMxnCents(c.totalCents)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button
          type="button"
          className="link link-hover text-xs text-base-content/55 mt-2"
          onClick={() => setManualMode((m) => !m)}
        >
          {manualMode ? "← Volver a candidatos sugeridos" : "¿No aparece? Modo manual →"}
        </button>

        {manualMode && (
          <div className="mt-3 border border-base-300 rounded-lg p-3">
            <p className="text-xs text-base-content/55 mb-2">
              Selecciona la otra cuenta. Se creará un transfer canónico sin pareja (la otra tx no
              está en la DB).
            </p>
            <label className="form-control block">
              <div className="label py-1">
                <span className="label-text text-sm font-medium">
                  {tx.kind === "ingreso" ? "Origen del dinero" : "Destino del dinero"}
                </span>
              </div>
              <select
                value={otherAccountId}
                onChange={(e) => setOtherAccountId(e.target.value)}
                className="select select-bordered w-full select-sm"
              >
                <option value="">— Selecciona —</option>
                {otherAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => convert.mutate({ otherAccountId })}
              disabled={!otherAccountId || convert.isPending}
              className="btn btn-info btn-sm mt-3 w-full"
            >
              {convert.isPending && <span className="loading loading-spinner loading-xs" />}
              Crear transferencia sin pareja
            </button>
          </div>
        )}

        {convert.isError && (
          <div className="alert alert-error text-sm mt-3">
            {convert.error instanceof Error ? convert.error.message : "Error"}
          </div>
        )}

        <div className="modal-action mt-4 pt-3 border-t border-base-300/60">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Modal para gestionar el adjunto de una transacción ya creada.
 * - Si tiene adjunto: muestra filename + botones descargar / quitar / cambiar.
 * - Si no tiene: invita a subir.
 *
 * Quitar = DELETE /finance/documents/:id (que también borra de R2) + PATCH
 * la transacción a fin_document_id=null. El doc desaparece del archivero.
 */
interface TxDocument {
  id: string;
  filename: string;
  sizeBytes: number;
  kind: string;
}

function AttachmentManagerDialog({
  tx,
  onClose,
}: {
  tx: Transaction & {
    attachmentFilename?: string | null;
    attachmentSize?: number | null;
    documents?: TxDocument[];
  };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<StagedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Lista canónica de documentos ya ligados. Hace una query independiente
  // por si el listado del feed no incluye `documents` (TX antigua antes
  // del refresh).
  const docsQuery = useQuery<{ documents: TxDocument[] }>({
    queryKey: ["finance", "tx-docs", tx.id],
    queryFn: () => api.get(`/finance/transactions/${tx.id}/documents`),
    initialData: tx.documents ? { documents: tx.documents } : undefined,
  });
  const attached = docsQuery.data?.documents ?? [];

  const defaultKind: DocKindUi =
    tx.kind === "ingreso"
      ? "factura_emitida_pdf"
      : tx.invoiceStatus === "facturado"
        ? "factura_recibida_pdf"
        : "otro";

  const uploadAll = async () => {
    if (pending.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const period = (tx.paymentDate ?? tx.date).slice(0, 7);
      for (const staged of pending) {
        const fd = new FormData();
        fd.append("file", staged.file);
        fd.append("period", period);
        fd.append("kind", uiKindToKind(staged.kind));
        const res = await fetch("/api/finance/documents", { method: "POST", body: fd });
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(err.error ?? `${res.status}`);
        }
        const json = (await res.json()) as { id: string };
        await api.post(`/finance/transactions/${tx.id}/documents`, { finDocumentId: json.id });
      }
      setPending([]);
      qc.invalidateQueries({ queryKey: ["finance"] });
      docsQuery.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const removeOne = async (docId: string) => {
    if (!confirm("¿Quitar este documento? También se borra del archivero.")) return;
    setBusy(true);
    setError(null);
    try {
      await api.del(`/finance/transactions/${tx.id}/documents/${docId}`);
      await api.del(`/finance/documents/${docId}`);
      qc.invalidateQueries({ queryKey: ["finance"] });
      docsQuery.refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-lg">
        <h3 className="font-display font-semibold text-lg mb-3">
          Documentos adjuntos
          {attached.length > 0 && (
            <span className="ml-2 text-sm text-base-content/55 tabular-nums">
              ({attached.length})
            </span>
          )}
        </h3>

        {attached.length === 0 ? (
          <p className="text-sm text-base-content/65 mb-3">
            Esta transacción no tiene documentos. Súbelos abajo (PDF + XML del CFDI, recibos, etc.).
          </p>
        ) : (
          <ul className="space-y-2 mb-4">
            {attached.map((d) => (
              <li
                key={d.id}
                className="rounded-md border border-success/40 bg-success/5 p-2.5 flex items-center gap-3"
              >
                <Paperclip className="size-4 text-success shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{d.filename}</div>
                  <div className="text-xs text-base-content/55">{formatBytes(d.sizeBytes)}</div>
                </div>
                <select
                  className="select select-bordered select-xs w-44"
                  value={DOC_KINDS.includes(d.kind as DocKind) ? d.kind : "otro"}
                  disabled={busy}
                  onChange={async (e) => {
                    setBusy(true);
                    setError(null);
                    try {
                      await api.patch(`/archivero/${d.id}`, { kind: e.target.value });
                      qc.invalidateQueries({ queryKey: ["finance"] });
                      docsQuery.refetch();
                    } catch (err) {
                      setError(err instanceof Error ? err.message : String(err));
                    } finally {
                      setBusy(false);
                    }
                  }}
                  title="Cambiar tipo de documento"
                >
                  {DOC_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {DOC_KIND_LABEL[k]}
                    </option>
                  ))}
                </select>
                <a
                  href={`/api/finance/documents/${d.id}/download`}
                  className="btn btn-ghost btn-xs btn-square"
                  title="Descargar"
                >
                  <ArrowRight className="size-3.5 -rotate-45" />
                </a>
                <button
                  type="button"
                  onClick={() => removeOne(d.id)}
                  disabled={busy}
                  className="btn btn-ghost btn-xs btn-square text-error"
                  aria-label="Quitar"
                  title="Quitar y borrar del archivero"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}

        <StagedFilesList
          files={pending}
          onChange={setPending}
          defaultKind={defaultKind}
          addLabel="Agregar archivos (PDF, XML, imagen) — puedes seleccionar varios"
          disabled={tx.documentedExternally}
          disabledHint="No hace falta: la tx ya tiene su documento declarado fuera del sistema."
        />

        {error && <div className="alert alert-error text-xs py-2 mt-3">{error}</div>}

        <div className="modal-action mt-4 pt-3 border-t border-base-300/60">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cerrar
          </button>
          {pending.length > 0 && (
            <button
              type="button"
              onClick={uploadAll}
              disabled={busy}
              className="btn btn-primary btn-sm"
            >
              {busy && <span className="loading loading-spinner loading-xs" />}
              Subir {pending.length}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TransferRow({
  transfer,
  fromName,
  toName,
  onEdit,
}: {
  transfer: Transfer;
  fromName?: string;
  toName?: string;
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.del(`/finance/transfers/${transfer.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finance"] }),
  });

  return (
    <li
      className={clsx(
        "group bg-base-100 hover:bg-base-200/40 transition-colors",
        "border-b border-base-300/40 last:border-b-0",
        "px-3 py-2.5 flex items-center gap-3",
      )}
    >
      <div
        className="size-9 rounded-full grid place-items-center shrink-0 bg-info/10 text-info"
        aria-hidden
      >
        <ArrowLeftRight className="size-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">
            {fromName ?? "—"}
            <ArrowRight className="size-3 inline mx-1.5 text-base-content/40" />
            {toName ?? "—"}
          </span>
          <span className="badge badge-xs badge-outline shrink-0 text-[10px] font-normal">
            Transferencia
          </span>
        </div>
        <div className="text-xs text-base-content/55 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span className="tabular-nums">{formatDate(transfer.date)}</span>
          {!transfer.affectsTotals && (
            <>
              <span className="text-base-content/30">•</span>
              <span className="text-base-content/50 text-[11px]">histórico</span>
            </>
          )}
          {transfer.notes && (
            <>
              <span className="text-base-content/30">•</span>
              <span className="italic truncate" title={transfer.notes}>
                {transfer.notes}
              </span>
            </>
          )}
        </div>
      </div>
      {/* Monto + saldo resultante de cada cuenta tras la transferencia.
          Mismo ancho fijo que TransactionRow para alinear columnas. */}
      <div className="text-right shrink-0 w-40">
        <div className="text-base font-bold tabular-nums text-info">
          {formatMxnCents(transfer.amountCents)}
        </div>
        {transfer.fromBalanceAfterCents != null && (
          <div
            className="text-[11px] text-base-content/55 tabular-nums truncate"
            title={`Saldo de ${fromName ?? "cuenta origen"} tras esta transferencia`}
          >
            {fromName ?? "Origen"} {formatMxnCents(transfer.fromBalanceAfterCents)}
          </div>
        )}
        {transfer.toBalanceAfterCents != null && (
          <div
            className="text-[11px] text-base-content/55 tabular-nums truncate"
            title={`Saldo de ${toName ?? "cuenta destino"} tras esta transferencia`}
          >
            {toName ?? "Destino"} {formatMxnCents(transfer.toBalanceAfterCents)}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-0.5 shrink-0 w-20 place-items-center">
        <button
          type="button"
          onClick={onEdit}
          className="btn btn-ghost btn-xs btn-square text-base-content/60 hover:text-primary transition-colors"
          aria-label="Editar"
          title="Editar"
        >
          <Pencil className="size-3.5" />
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm("¿Eliminar transferencia?")) remove.mutate();
          }}
          disabled={remove.isPending}
          className="btn btn-ghost btn-xs btn-square text-base-content/60 hover:text-error transition-colors"
          aria-label="Eliminar"
          title="Eliminar"
        >
          <Trash2 className="size-3.5" />
        </button>
        {/* Slot fantasma en la última columna para alinear las dos visibles
            con Paperclip + Pencil de TransactionRow. */}
        <span className="btn btn-ghost btn-xs btn-square invisible" aria-hidden />
      </div>
    </li>
  );
}

// ─── Income / Expense dialogs ─────────────────────────────────────────────

function IncomeDialog({ onClose, existingTx }: { onClose: () => void; existingTx?: Transaction }) {
  return (
    <TransactionDialog
      kind="ingreso"
      title={existingTx ? "Editar ingreso" : "Nuevo ingreso"}
      accentClass="text-success"
      counterpartyLabel="Cliente"
      paymentStatusOptions={[
        { value: "cobrado", label: "Cobrado" },
        { value: "pendiente", label: "Por cobrar" },
      ]}
      paymentDateLabel="Fecha de cobro"
      paymentDateHint="Vacío si aún no se ha cobrado."
      invoiceLabel="Factura emitida (CFDI)"
      onClose={onClose}
      existingTx={existingTx}
    />
  );
}

function ExpenseDialog({ onClose, existingTx }: { onClose: () => void; existingTx?: Transaction }) {
  return (
    <TransactionDialog
      kind="gasto"
      title={existingTx ? "Editar gasto" : "Nuevo gasto"}
      accentClass="text-error"
      counterpartyLabel="Proveedor"
      paymentStatusOptions={[
        { value: "pagado", label: "Pagado" },
        { value: "pendiente", label: "Por pagar" },
      ]}
      paymentDateLabel="Fecha de pago"
      paymentDateHint="Vacío si aún no se ha pagado."
      invoiceLabel="Factura recibida (CFDI)"
      showPaymentMethod
      onClose={onClose}
      existingTx={existingTx}
    />
  );
}

interface TransactionDialogProps {
  kind: "ingreso" | "gasto";
  title: string;
  accentClass: string;
  counterpartyLabel: string;
  paymentStatusOptions: { value: string; label: string }[];
  paymentDateLabel: string;
  paymentDateHint: string;
  invoiceLabel: string;
  showPaymentMethod?: boolean;
  onClose: () => void;
  existingTx?: Transaction;
}

function TransactionDialog({
  kind,
  title,
  accentClass,
  counterpartyLabel,
  paymentStatusOptions,
  paymentDateLabel,
  paymentDateHint,
  invoiceLabel,
  showPaymentMethod,
  onClose,
  existingTx,
}: TransactionDialogProps) {
  const qc = useQueryClient();
  const accounts = useQuery<{ accounts: Account[] }>({
    queryKey: ["finance", "accounts"],
    queryFn: () => api.get("/finance/accounts"),
  });
  const categories = useQuery<{ categories: Category[] }>({
    queryKey: ["finance", "categories"],
    queryFn: () => api.get("/finance/categories"),
  });
  const projects = useQuery<{ projects: Array<{ id: string; name: string; type: string }> }>({
    queryKey: ["projects", "for-transactions"],
    queryFn: () => api.get("/projects?status=active,paused,completed"),
  });

  const today = new Date().toISOString().slice(0, 10);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<TransactionCreateInput>({
    resolver: zodResolver(transactionCreateSchema),
    defaultValues: existingTx
      ? {
          accountId: existingTx.accountId,
          date: existingTx.date,
          kind: existingTx.kind as "ingreso" | "gasto",
          paymentMethod: existingTx.paymentMethod ?? undefined,
          recurrence: existingTx.recurrence,
          categoryId: existingTx.categoryId ?? undefined,
          companyId: existingTx.companyId ?? undefined,
          counterparty: existingTx.counterparty ?? undefined,
          title: existingTx.title ?? undefined,
          description: existingTx.description ?? undefined,
          subtotalCents: existingTx.subtotalCents,
          ivaCents: existingTx.ivaCents,
          totalCents: existingTx.totalCents,
          invoiceStatus: existingTx.invoiceStatus,
          invoiceUuid: existingTx.invoiceUuid ?? undefined,
          paymentStatus: existingTx.paymentStatus,
          paymentDate: existingTx.paymentDate ?? undefined,
          affectsTotals: existingTx.affectsTotals,
          isrApplicable: existingTx.isrApplicable,
          documentedExternally: existingTx.documentedExternally ?? false,
          projectId: existingTx.projectId ?? undefined,
          notes: existingTx.notes ?? undefined,
          isAsimilado: existingTx.isAsimilado ?? false,
          beneficiaryName: existingTx.beneficiaryName ?? undefined,
          beneficiaryRfc: existingTx.beneficiaryRfc ?? undefined,
          beneficiaryCurp: existingTx.beneficiaryCurp ?? undefined,
          isrRetenidoCents: existingTx.isrRetenidoCents ?? undefined,
          payrollUserId: existingTx.payrollUserId ?? undefined,
          isSatPayment: existingTx.isSatPayment ?? false,
          satLineaCaptura: existingTx.satLineaCaptura ?? undefined,
          satPeriod: existingTx.satPeriod ?? undefined,
        }
      : {
          date: today,
          kind,
          paymentMethod: kind === "gasto" ? "transfer" : undefined,
          recurrence: "none",
          invoiceStatus: "facturado",
          paymentStatus: kind === "ingreso" ? "cobrado" : "pagado",
          paymentDate: today,
          affectsTotals: true,
          isrApplicable: true,
          subtotalCents: 0,
          ivaCents: 0,
          totalCents: 0,
          isAsimilado: false,
          isSatPayment: false,
        },
  });

  const subtotal = useWatch({ control, name: "subtotalCents" }) ?? 0;
  const total = useWatch({ control, name: "totalCents" }) ?? 0;
  const iva = useWatch({ control, name: "ivaCents" }) ?? 0;
  const categoryId = useWatch({ control, name: "categoryId" });
  const invoiceStatus = useWatch({ control, name: "invoiceStatus" });
  const accountIdWatch = useWatch({ control, name: "accountId" });
  const paymentMethodWatch = useWatch({ control, name: "paymentMethod" });

  // Métodos de pago permitidos según la cuenta seleccionada. Si la cuenta
  // no tiene la columna (migración pendiente) o no aparece, caemos a todos
  // los métodos para no bloquear al usuario.
  const selectedAccount = (accounts.data?.accounts ?? []).find((a) => a.id === accountIdWatch);
  const allowedMethods: PaymentMethod[] = (
    selectedAccount?.allowedPaymentMethods?.length
      ? selectedAccount.allowedPaymentMethods
      : [...PAYMENT_METHODS]
  ) as PaymentMethod[];
  const accountDefaultMethod = (selectedAccount?.defaultPaymentMethod ??
    null) as PaymentMethod | null;
  const lockedMethod = allowedMethods.length === 1 ? allowedMethods[0] : null;

  // Regla del método de pago:
  //  (a) Si la cuenta solo permite uno (lockedMethod), forzarlo SIEMPRE
  //      — incluso al montar en edit-mode. Si la tx vieja tenía otro
  //      método porque la config de la cuenta cambió, queremos corregirlo
  //      al guardar; el select disabled no debe quedar en un valor inválido.
  //  (b) Si la cuenta permite varios y el usuario cambia de cuenta, y el
  //      método actual no está entre los permitidos, lo reseteamos al
  //      default de la cuenta (o al primero permitido). Si el método ya
  //      es válido, lo dejamos como está (no pisamos lo que el usuario
  //      ya tenía).
  const prevAccountIdRef = useRef<string | undefined>(accountIdWatch);
  useEffect(() => {
    const prev = prevAccountIdRef.current;
    prevAccountIdRef.current = accountIdWatch;
    if (!selectedAccount) return;
    const current = paymentMethodWatch as PaymentMethod | undefined;
    if (lockedMethod) {
      if (current !== lockedMethod) setValue("paymentMethod", lockedMethod);
      return;
    }
    // Multi-método: solo resetear cuando el usuario cambió de cuenta
    // (no en el mount inicial) y el método actual no es válido.
    if (prev === accountIdWatch) return;
    if (!current || !allowedMethods.includes(current)) {
      const next = accountDefaultMethod ?? allowedMethods[0];
      if (next) setValue("paymentMethod", next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountIdWatch, selectedAccount?.id]);

  // Categorías no sujetas a IVA: nóminas, impuestos, comisiones, intereses.
  // Si seleccionas una de esas, IVA se fija en exento (0) y no se puede editar.
  // También si invoice_status=deducible_sin_factura: no hay CFDI → no hay IVA
  // acreditable, así que el campo deja de tener sentido. Y los pagos al SAT
  // (isSatPayment) no llevan IVA al nivel del padre — el bruto es la suma
  // total de lo que salió del banco; el desglose IVA/ISR vive en splits.
  const selectedCategory = (categories.data?.categories ?? []).find((c) => c.id === categoryId);
  const isSatPaymentParent = useWatch({ control, name: "isSatPayment" }) ?? false;
  const isAsimiladoParent = useWatch({ control, name: "isAsimilado" }) ?? false;
  // Al activar asimilado, normalizamos invoiceStatus → 'facturado'. La
  // empresa emite el CFDI de nómina/asimilados; no hay CFDI "pendiente"
  // ni "olvidado" desde el lado del receptor. El campo se esconde abajo.
  useEffect(() => {
    if (isAsimiladoParent) setValue("invoiceStatus", "facturado", { shouldDirty: false });
  }, [isAsimiladoParent, setValue]);
  const documentedExternallyWatch = useWatch({ control, name: "documentedExternally" }) ?? false;
  const ivaLocked =
    (selectedCategory && !selectedCategory.ivaApplicable) ||
    invoiceStatus === "deducible_sin_factura" ||
    invoiceStatus === "no_aplica" ||
    invoiceStatus === "olvidado" ||
    isSatPaymentParent ||
    isAsimiladoParent;

  // Tasa de IVA: 16% general, 8% frontera, 0% tasa cero, exempt sin IVA,
  // custom para casos manuales. Local state — el server solo guarda el monto
  // (ivaCents) y la tasa se infiere del ratio iva/subtotal al re-abrir en
  // modo edit.
  const [ivaRate, setIvaRate] = useState<IvaRate>(() => {
    if (existingTx) return inferIvaRate(existingTx.subtotalCents, existingTx.ivaCents, ivaLocked);
    return ivaLocked ? "exempt" : "16";
  });

  // Cuando se activa el lock, forzar exento y mover el IVA al subtotal
  // (el TOTAL bruto se mantiene estable — lo que realmente salió/entró
  // del banco no cambia, solo deja de tener desglose fiscal).
  useEffect(() => {
    setIvaRate((prev) => {
      if (ivaLocked) return "exempt";
      if (prev === "exempt") return "16";
      return prev;
    });
    if (ivaLocked) {
      const sub = typeof subtotal === "number" ? subtotal : 0;
      const ivaNow = typeof iva === "number" ? iva : 0;
      const totalNow = sub + ivaNow;
      setValue("subtotalCents", totalNow, { shouldValidate: true });
      setValue("ivaCents", 0);
      setValue("totalCents", totalNow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ivaLocked]);

  // Cuando el status pasa a "facturado" durante la edición y aún no hay
  // IVA registrado (vino de pendiente / import), desglosamos 16% del
  // total actual: total estable, subtotal e IVA se calculan hacia atrás.
  // El ref evita disparar esto en el mount inicial (cuando ya está en
  // "facturado" desde el principio).
  const prevInvoiceStatusRef = useRef(invoiceStatus);
  useEffect(() => {
    const prev = prevInvoiceStatusRef.current;
    prevInvoiceStatusRef.current = invoiceStatus;
    if (prev === invoiceStatus) return;
    if (invoiceStatus !== "facturado" || ivaLocked) return;
    const sub = typeof subtotal === "number" ? subtotal : 0;
    const ivaNow = typeof iva === "number" ? iva : 0;
    const totalNow = sub + ivaNow;
    if (ivaNow !== 0 || totalNow === 0) return;
    const newSub = Math.round(totalNow / 1.16);
    const newIva = totalNow - newSub;
    setValue("subtotalCents", newSub, { shouldValidate: true });
    setValue("ivaCents", newIva);
    setValue("totalCents", totalNow);
    setIvaRate("16");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceStatus]);

  const onSubtotalChange = (v: number) => {
    setValue("subtotalCents", v, { shouldValidate: true });
    if (ivaRate !== "custom") {
      const newIva = computeIvaForRate(v, ivaRate);
      setValue("ivaCents", newIva);
      setValue("totalCents", v + newIva);
    } else {
      setValue("totalCents", v + (typeof iva === "number" ? iva : 0));
    }
  };
  const onIvaChange = (v: number) => {
    setValue("ivaCents", v, { shouldValidate: true });
    setValue("totalCents", (typeof subtotal === "number" ? subtotal : 0) + v);
  };
  const onTotalChange = (v: number) => {
    // Cálculo inverso: el usuario escribe el total y desglosamos hacia atrás
    // según la tasa actual. Útil cuando llega una factura ya con total
    // (ej. SaaS extranjero $116 = $100 + 16% IVA).
    if (ivaRate === "custom") {
      // En custom no podemos inferir; solo respetamos el subtotal actual
      // y ajustamos IVA = total - subtotal.
      const sub = typeof subtotal === "number" ? subtotal : 0;
      setValue("ivaCents", Math.max(0, v - sub), { shouldValidate: true });
      setValue("totalCents", v);
      return;
    }
    const pct = ivaRate === "16" ? 0.16 : ivaRate === "8" ? 0.08 : 0;
    const newSub = Math.round(v / (1 + pct));
    const newIva = v - newSub;
    setValue("subtotalCents", newSub, { shouldValidate: true });
    setValue("ivaCents", newIva);
    setValue("totalCents", v);
  };
  const onIvaRateChange = (rate: IvaRate) => {
    setIvaRate(rate);
    if (rate !== "custom") {
      const subValue = typeof subtotal === "number" ? subtotal : 0;
      const newIva = computeIvaForRate(subValue, rate);
      setValue("ivaCents", newIva);
      setValue("totalCents", subValue + newIva);
    }
  };

  const [attachments, setAttachments] = useState<StagedFile[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadStage, setUploadStage] = useState<"idle" | "creating" | "uploading" | "linking">(
    "idle",
  );

  // Comisión de plataforma de pago (Stripe, PayPal, etc.). Solo aplica a
  // ingresos. Cuando active=true al guardar, además del ingreso se crea
  // una tx-gasto pareja para la comisión con la categoría
  // "Comisiones de plataforma de pago".
  const [platformCom, setPlatformCom] = useState<{
    active: boolean;
    platform: string;
    commissionCents: number;
    hasCfdi: boolean;
  }>({ active: false, platform: "Stripe", commissionCents: 0, hasCfdi: false });

  // Splits (sub-ingresos / sub-gastos). El padre conserva el total bruto;
  // los splits reparten ese total por categoría / proyecto / contraparte.
  // En modo edit, hidratamos desde existingTx.splits.
  type SplitDraft = {
    id?: string;
    categoryId: string | null;
    projectId: string | null;
    companyId: string | null;
    description: string | null;
    subtotalCents: number;
    ivaCents: number;
    totalCents: number;
  };
  const [splits, setSplits] = useState<SplitDraft[]>(() => {
    const existing = (existingTx as (Transaction & { splits?: SplitDraft[] }) | undefined)?.splits;
    if (!existing || existing.length === 0) return [];
    return existing.map((s) => ({
      id: s.id,
      categoryId: s.categoryId ?? null,
      projectId: s.projectId ?? null,
      companyId: s.companyId ?? null,
      description: s.description ?? null,
      subtotalCents: s.subtotalCents,
      ivaCents: s.ivaCents,
      totalCents: s.totalCents,
    }));
  });

  // Default kind sugerido para nuevos adjuntos según tipo de tx y status
  // de factura. ingreso → factura_emitida_pdf; gasto + facturado →
  // factura_recibida_pdf; resto → otro. La heurística por archivo ajusta
  // PDF/XML según la extensión.
  const defaultDocKind: DocKindUi =
    kind === "ingreso"
      ? "factura_emitida_pdf"
      : invoiceStatus === "facturado"
        ? "factura_recibida_pdf"
        : "otro";

  const submit = async (input: TransactionCreateInput) => {
    setSubmitError(null);
    try {
      // 1. Crear/actualizar la tx primero (sin docs).
      setUploadStage("creating");
      let txId = existingTx?.id;
      if (existingTx) {
        await api.patch(`/finance/transactions/${existingTx.id}`, input);
      } else {
        const created = (await api.post("/finance/transactions", input)) as { id: string };
        txId = created.id;
      }

      // 1b. Comisión de plataforma — solo en creación de ingreso. Crea
      // una tx-gasto pareja con la misma cuenta y fecha de cobro. El
      // CFDI mexicano (Stripe MX) trae IVA acreditable; sin CFDI
      // (Stripe US/IE) va como deducible_sin_factura.
      if (
        !existingTx &&
        kind === "ingreso" &&
        platformCom.active &&
        platformCom.commissionCents > 0
      ) {
        const com = platformCom.commissionCents;
        const sub = platformCom.hasCfdi ? Math.round(com / 1.16) : com;
        const iva = platformCom.hasCfdi ? com - sub : 0;
        await api.post("/finance/transactions", {
          accountId: input.accountId,
          date: input.date,
          kind: "gasto",
          paymentMethod: "transfer",
          recurrence: "none",
          categoryId: "cat_comisiones_plataforma",
          counterparty: platformCom.platform,
          title: `Comisión ${platformCom.platform}`,
          description: txId
            ? `Comisión de plataforma asociada al ingreso ${txId.slice(0, 8)}`
            : undefined,
          subtotalCents: sub,
          ivaCents: iva,
          totalCents: com,
          invoiceStatus: platformCom.hasCfdi ? "facturado" : "deducible_sin_factura",
          paymentStatus: "pagado",
          paymentDate: input.paymentDate ?? input.date,
          affectsTotals: true,
          isrApplicable: true,
        });
      }

      // 2. Subir cada adjunto con su propio kind y atacharlo vía join.
      if (txId && attachments.length > 0) {
        setUploadStage("uploading");
        const period = (input.paymentDate ?? input.date).slice(0, 7);
        for (const staged of attachments) {
          const fd = new FormData();
          fd.append("file", staged.file);
          fd.append("period", period);
          fd.append("kind", uiKindToKind(staged.kind));
          const res = await fetch("/api/finance/documents", { method: "POST", body: fd });
          if (!res.ok) {
            const err = (await res.json().catch(() => ({}))) as { error?: string };
            throw new Error(`Subida falló: ${err.error ?? res.status}`);
          }
          const json = (await res.json()) as { id: string };
          await api.post(`/finance/transactions/${txId}/documents`, { finDocumentId: json.id });
        }
      }

      // 3. Splits: PUT bulk replace. La suma debe igualar total del padre
      // (el backend valida); aquí solo lo enviamos. Lista vacía borra todos
      // los splits del padre.
      if (txId) {
        await api.put(`/finance/transactions/${txId}/splits`, {
          splits: splits.map((s, i) => ({ ...s, position: i })),
        });
      }

      qc.invalidateQueries({ queryKey: ["finance"] });
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
      setUploadStage("idle");
    }
  };

  const filteredCategories = (categories.data?.categories ?? []).filter((c) => c.kind === kind);

  return (
    <ModalShell title={title} accentClass={accentClass} onClose={onClose}>
      <form onSubmit={handleSubmit(submit)} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Cuenta" required error={errors.accountId?.message}>
            <select className="select select-bordered w-full" {...register("accountId")}>
              <option value="">—</option>
              {accounts.data?.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Categoría">
            <select className="select select-bordered w-full" {...register("categoryId")}>
              <option value="">—</option>
              {filteredCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field
            label={counterpartyLabel}
            help="Si no existe, escribe el nombre y dale a Crear desde el dropdown."
          >
            <Controller
              control={control}
              name="companyId"
              render={({ field }) => (
                <CompanyPicker
                  value={field.value ?? undefined}
                  onChange={(v) => field.onChange(v ?? "")}
                  onlySuppliers={kind === "gasto"}
                  onlyClients={kind === "ingreso"}
                />
              )}
            />
          </Field>
          <Field
            label="Proyecto"
            help={
              kind === "ingreso"
                ? "Liga el ingreso al proyecto que lo generó."
                : "Vacío para gastos generales de Axorial."
            }
          >
            <select className="select select-bordered w-full" {...register("projectId")}>
              <option value="">— Axorial (general) —</option>
              {(projects.data?.projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.type === "client" ? "· cliente" : "· interno"}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field
            label="Título"
            help="Texto corto que se ve grande en la fila. Si lo dejas vacío usa la descripción o la contraparte."
          >
            <input
              className="input input-bordered w-full"
              placeholder="Ej. Licencia Adobe anual"
              {...register("title")}
            />
          </Field>
          <Field label="Descripción">
            <input
              className="input input-bordered w-full"
              placeholder="Concepto o detalle del banco"
              {...register("description")}
            />
          </Field>
        </div>

        <AmountSection
          control={control}
          subtotal={typeof subtotal === "number" ? subtotal : 0}
          iva={typeof iva === "number" ? iva : 0}
          ivaRate={ivaRate}
          onSubtotal={onSubtotalChange}
          onIva={onIvaChange}
          onTotal={onTotalChange}
          onIvaRateChange={onIvaRateChange}
          ivaLocked={ivaLocked}
          ivaLockedReason={
            isAsimiladoParent
              ? "Asimilado Art. 96 — Monto = neto al trabajador. Bruto y retención abajo."
              : isSatPaymentParent
                ? "Pago al SAT — desglose IVA/ISR/otros vive en splits."
                : invoiceStatus === "deducible_sin_factura"
                  ? "Sin factura → sin IVA acreditable."
                  : invoiceStatus === "no_aplica"
                    ? "Gasto no deducible."
                    : invoiceStatus === "olvidado"
                      ? "Sin CFDI emitido a tiempo → deducción perdida."
                      : selectedCategory && !selectedCategory.ivaApplicable
                        ? `Categoría "${selectedCategory.name}" no acumula IVA.`
                        : undefined
          }
          totalError={errors.totalCents?.message}
        />

        <div
          className={clsx(
            "grid grid-cols-1 sm:grid-cols-2 gap-3",
            showPaymentMethod ? "lg:grid-cols-5" : "lg:grid-cols-4",
          )}
        >
          <Field label="Fecha (devengado)" required>
            <input type="date" className="input input-bordered w-full" {...register("date")} />
          </Field>
          <Field label={paymentDateLabel} help={paymentDateHint}>
            <input
              type="date"
              className="input input-bordered w-full"
              {...register("paymentDate")}
            />
          </Field>
          {showPaymentMethod && (
            <Field
              label="Método de pago"
              help={lockedMethod ? "La cuenta solo permite este método." : undefined}
            >
              <select
                className="select select-bordered w-full"
                disabled={!!lockedMethod}
                {...register("paymentMethod")}
              >
                {allowedMethods.map((m) => (
                  <option key={m} value={m}>
                    {PAYMENT_METHOD_LABELS[m]}
                  </option>
                ))}
                {/* Si la tx ya tenía un método que ya no está permitido para
                    esta cuenta (config cambió posterior), lo mostramos para no
                    perder data — el usuario puede dejarlo o reemplazarlo. */}
                {paymentMethodWatch &&
                  !allowedMethods.includes(paymentMethodWatch as PaymentMethod) && (
                    <option value={paymentMethodWatch}>
                      {PAYMENT_METHOD_LABELS[paymentMethodWatch as PaymentMethod]} · ya no permitido
                    </option>
                  )}
              </select>
            </Field>
          )}
          <Field label="Status de pago" required>
            <select className="select select-bordered w-full" {...register("paymentStatus")}>
              {paymentStatusOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          {/* Para asimilados a salarios no hay CFDI recibido: la empresa
              emite el CFDI de nómina/asimilados, no lo recibe. La
              deducibilidad la maneja el flag isAsimilado en el resumen,
              no este campo. */}
          {!isAsimiladoParent && (
            <Field
              label={invoiceLabel}
              help={
                kind === "gasto"
                  ? "SaaS / proveedor extranjero sin CFDI mexicano → 'Deducible sin factura'."
                  : undefined
              }
            >
              <select className="select select-bordered w-full" {...register("invoiceStatus")}>
                <option value="facturado">Con factura (CFDI)</option>
                {kind === "gasto" && (
                  <option value="deducible_sin_factura">
                    Sin CFDI · deducible (extranjero, recibo)
                  </option>
                )}
                <option value="pendiente">Pendiente de facturar</option>
                <option value="no_aplica">No deducible (gasto personal, etc.)</option>
                <option value="olvidado">Olvidada · no facturada a tiempo</option>
              </select>
            </Field>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Recurrencia" help="Solo afecta proyecciones what-if.">
            <select className="select select-bordered w-full" {...register("recurrence")}>
              <option value="none">Una vez</option>
              <option value="weekly">Semanal</option>
              <option value="monthly">Mensual</option>
              <option value="quarterly">Trimestral</option>
              <option value="yearly">Anual</option>
            </select>
          </Field>
          <AttachmentField
            files={attachments}
            onChange={setAttachments}
            defaultKind={defaultDocKind}
            disabled={documentedExternallyWatch}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <AffectsTotalsToggle register={register("affectsTotals")} />
          <IsrApplicableToggle register={register("isrApplicable")} kind={kind} />
        </div>

        <DocumentedExternallyToggle register={register("documentedExternally")} kind={kind} />

        {kind === "gasto" && (
          <>
            <AsimiladoSection control={control} register={register} setValue={setValue} />
            <SatPaymentSection
              control={control}
              register={register}
              setValue={setValue}
              splits={splits}
              onSplitsChange={setSplits}
              parentTotalCents={typeof total === "number" ? total : subtotal + iva}
            />
          </>
        )}

        {!existingTx && kind === "ingreso" && (
          <PlatformCommissionSection
            value={platformCom}
            onChange={setPlatformCom}
            ingresoTotalCents={typeof total === "number" ? total : subtotal + iva}
          />
        )}

        <SplitsSection
          splits={splits}
          onChange={setSplits}
          parentTotalCents={typeof total === "number" ? total : subtotal + iva}
          parentSubtotalCents={typeof subtotal === "number" ? subtotal : 0}
          parentIvaCents={typeof iva === "number" ? iva : 0}
          kind={kind}
          categories={filteredCategories}
          projects={projects.data?.projects ?? []}
        />

        <Field label="Notas">
          <textarea rows={2} className="textarea textarea-bordered w-full" {...register("notes")} />
        </Field>

        {submitError && <div className="alert alert-error text-sm py-2">{submitError}</div>}

        <DialogActions
          submitLabel={
            uploadStage === "uploading"
              ? "Subiendo factura…"
              : kind === "ingreso"
                ? "Registrar ingreso"
                : "Registrar gasto"
          }
          submitting={uploadStage !== "idle"}
          onClose={onClose}
        />
      </form>
    </ModalShell>
  );
}

interface StagedFile {
  file: File;
  kind: DocKindUi;
}

/**
 * Lista controlada de archivos pendientes de subir con selector de tipo
 * por archivo. Compartido entre AttachmentField (modal de tx) y la cola
 * de subida del AttachmentManagerDialog. El "kind" es de UI (incluye la
 * subdivisión PDF/XML para facturas); se mapea al kind de BD al subir.
 */
function StagedFilesList({
  files,
  onChange,
  defaultKind,
  addLabel = "Agregar archivos",
  disabled = false,
  disabledHint,
}: {
  files: StagedFile[];
  onChange: (next: StagedFile[]) => void;
  defaultKind: DocKindUi;
  addLabel?: string;
  disabled?: boolean;
  disabledHint?: string;
}) {
  const acceptPicked = (picked: File[]) => {
    if (picked.length === 0) return;
    const staged: StagedFile[] = picked.map((file) => ({
      file,
      kind: guessDocKindUi(file, defaultKind),
    }));
    onChange([...files, ...staged]);
  };
  return (
    <div className="space-y-1.5">
      {files.map((staged, i) => (
        <div
          key={`${staged.file.name}-${i}`}
          className="flex items-center gap-2 px-3 py-2 rounded-md border border-success/40 bg-success/5 text-sm"
        >
          <Paperclip className="size-4 text-success shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate text-sm">{staged.file.name}</div>
            <div className="text-[11px] text-base-content/55">{formatBytes(staged.file.size)}</div>
          </div>
          <select
            className="select select-bordered select-xs max-w-[12rem]"
            value={staged.kind}
            onChange={(e) => {
              const next = [...files];
              next[i] = { ...staged, kind: e.target.value as DocKindUi };
              onChange(next);
            }}
            aria-label={`Tipo de ${staged.file.name}`}
          >
            {DOC_KINDS_UI.map((k) => (
              <option key={k} value={k}>
                {DOC_KIND_UI_LABEL[k]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => onChange(files.filter((_, idx) => idx !== i))}
            className="btn btn-ghost btn-xs btn-square text-base-content/55 hover:text-error"
            aria-label="Quitar archivo"
          >
            <X className="size-3.5" />
          </button>
        </div>
      ))}
      {disabled ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-info/40 bg-info/10 text-sm text-info">
          <CheckCircle2 className="size-4 shrink-0" />
          <span className="truncate">
            {disabledHint ?? "Documento declarado fuera del sistema — no hace falta subirlo aquí."}
          </span>
        </div>
      ) : (
        <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-base-300/80 hover:border-primary/40 hover:bg-base-200/40 text-sm text-base-content/65">
          <Paperclip className="size-4 shrink-0" />
          <span>{addLabel}</span>
          <input
            type="file"
            className="hidden"
            multiple
            accept=".pdf,.xml,image/*,.zip"
            onChange={(e) => {
              acceptPicked(e.target.files ? Array.from(e.target.files) : []);
              e.target.value = "";
            }}
          />
        </label>
      )}
    </div>
  );
}

function AttachmentField({
  files,
  onChange,
  defaultKind,
  disabled = false,
}: {
  files: StagedFile[];
  onChange: (next: StagedFile[]) => void;
  defaultKind: DocKindUi;
  disabled?: boolean;
}) {
  return (
    <Field
      label={`Adjuntar documentos${files.length > 0 ? ` (${files.length})` : ""}`}
      help={
        disabled
          ? "No hace falta subirlo: la tx está marcada como documento fuera del sistema."
          : "CFDI: sube PDF + XML por separado y marca el tipo de cada uno. Se guardan en /finance/archivero y quedan ligados a esta transacción."
      }
    >
      <StagedFilesList
        files={files}
        onChange={onChange}
        defaultKind={defaultKind}
        disabled={disabled}
        addLabel={
          files.length === 0
            ? "Selecciona archivos (PDF, XML, imagen) — puedes elegir varios"
            : "Agregar más archivos"
        }
      />
    </Field>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function IsrApplicableToggle({
  register,
  kind,
}: {
  register: ReturnType<ReturnType<typeof useForm<TransactionCreateInput>>["register"]>;
  kind: "ingreso" | "gasto";
}) {
  return (
    <ToggleCard
      title="Aplica para ISR / IVA"
      description={
        kind === "ingreso"
          ? "Desactiva si NO es ingreso fiscal: préstamo recibido, devolución, regalo. Sigue afectando caja."
          : "Desactiva si NO es gasto deducible: pago personal hecho desde la cuenta de la empresa. Sigue afectando caja."
      }
      register={register}
    />
  );
}

function DocumentedExternallyToggle({
  register,
  kind,
}: {
  register: ReturnType<ReturnType<typeof useForm<TransactionCreateInput>>["register"]>;
  kind: "ingreso" | "gasto";
}) {
  return (
    <ToggleCard
      title="Tengo el documento fuera del sistema"
      description={
        kind === "ingreso"
          ? "Marca si emitiste el CFDI pero lo tienes en otro lado (email, PAC, contador). Evita que aparezca en facturas pendientes."
          : "Marca si tienes el CFDI / recibo en email o físico y no lo vas a subir aquí. Evita que aparezca en facturas pendientes."
      }
      register={register}
    />
  );
}

function AffectsTotalsToggle({
  register,
}: {
  register: ReturnType<ReturnType<typeof useForm<TransactionCreateInput>>["register"]>;
}) {
  return (
    <ToggleCard
      title="Afecta saldos y totales del mes"
      description="Desactiva si es histórico ya reflejado en el saldo inicial. Aparece en reportes pero no en el flujo del periodo."
      register={register}
    />
  );
}

function ToggleCard({
  title,
  description,
  register,
}: {
  title: string;
  description: string;
  register: ReturnType<ReturnType<typeof useForm<TransactionCreateInput>>["register"]>;
}) {
  // Importante: NO usar defaultChecked aquí. defaultChecked sobreescribiría
  // el valor que viene del form en mode edit (cuando affectsTotals/isrApplicable
  // estaban en false). react-hook-form controla el `checked` vía spread de
  // register desde defaultValues.
  return (
    <label className="cursor-pointer flex items-start gap-3 rounded-md border border-base-300/60 bg-base-200/30 p-3 min-w-0">
      <input
        type="checkbox"
        className="toggle toggle-sm toggle-primary shrink-0 mt-0.5"
        {...register}
      />
      <div className="text-left min-w-0 flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">{description}</div>
      </div>
    </label>
  );
}

// ─── Transfer dialog ──────────────────────────────────────────────────────

function TransferDialog({
  onClose,
  existingTransfer,
}: {
  onClose: () => void;
  existingTransfer?: Transfer;
}) {
  const qc = useQueryClient();
  const accounts = useQuery<{ accounts: Account[] }>({
    queryKey: ["finance", "accounts"],
    queryFn: () => api.get("/finance/accounts"),
  });

  const today = new Date().toISOString().slice(0, 10);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<TransferCreateInput>({
    resolver: zodResolver(transferCreateSchema),
    defaultValues: existingTransfer
      ? {
          fromAccountId: existingTransfer.fromAccountId,
          toAccountId: existingTransfer.toAccountId,
          amountCents: existingTransfer.amountCents,
          date: existingTransfer.date,
          affectsTotals: existingTransfer.affectsTotals,
          notes: existingTransfer.notes ?? undefined,
        }
      : { date: today, amountCents: 0 },
  });

  const fromId = useWatch({ control, name: "fromAccountId" });
  const toId = useWatch({ control, name: "toAccountId" });
  const amount = useWatch({ control, name: "amountCents" }) ?? 0;
  const sameAccount = !!fromId && fromId === toId;

  const save = useMutation({
    mutationFn: (input: TransferCreateInput) =>
      existingTransfer
        ? api.patch(`/finance/transfers/${existingTransfer.id}`, input)
        : api.post("/finance/transfers", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance"] });
      onClose();
    },
  });

  return (
    <ModalShell
      title={existingTransfer ? "Editar transferencia" : "Nueva transferencia"}
      accentClass="text-info"
      onClose={onClose}
      width="2xl"
    >
      <form
        onSubmit={handleSubmit((d) => {
          if (sameAccount) return;
          save.mutate(d);
        })}
        className="space-y-3"
      >
        <p className="text-xs text-base-content/60 leading-relaxed">
          Movimiento entre cuentas propias. No afecta ISR ni IVA — solo redistribuye el saldo.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-3 items-center">
          <Field label="Desde" required error={errors.fromAccountId?.message}>
            <select className="select select-bordered w-full" {...register("fromAccountId")}>
              <option value="">—</option>
              {accounts.data?.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
          <ArrowRight className="size-5 text-base-content/40 mt-7 hidden sm:block" />
          <Field label="Hacia" required error={errors.toAccountId?.message}>
            <select className="select select-bordered w-full" {...register("toAccountId")}>
              <option value="">—</option>
              {accounts.data?.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {sameAccount && (
          <div className="alert alert-warning text-sm py-2">
            Origen y destino no pueden ser la misma cuenta.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Monto" required error={errors.amountCents?.message}>
            <CentsInput
              value={typeof amount === "number" ? amount : 0}
              onChange={(v) => setValue("amountCents", v, { shouldValidate: true })}
            />
          </Field>
          <Field label="Fecha" required>
            <input type="date" className="input input-bordered w-full" {...register("date")} />
          </Field>
        </div>

        <Field label="Notas">
          <textarea
            rows={2}
            className="textarea textarea-bordered w-full"
            placeholder="Ej. Pago de tarjeta Konfio desde BBVA"
            {...register("notes")}
          />
        </Field>

        <div className="rounded-md border border-base-300/60 bg-base-200/30 p-3">
          <label className="cursor-pointer label justify-start gap-3 py-0">
            <input
              type="checkbox"
              defaultChecked
              className="toggle toggle-sm toggle-primary"
              {...register("affectsTotals")}
            />
            <div className="text-left">
              <div className="text-sm font-medium">Afecta saldos actuales</div>
              <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">
                Desactiva si es un movimiento histórico. Solo aparecerá en reportes.
              </div>
            </div>
          </label>
        </div>

        <DialogActions
          submitLabel={existingTransfer ? "Guardar cambios" : "Registrar transferencia"}
          submitting={save.isPending}
          submitDisabled={sameAccount || amount <= 0}
          onClose={onClose}
        />
      </form>
    </ModalShell>
  );
}

// ─── Shared modal pieces ──────────────────────────────────────────────────

function ModalShell({
  title,
  accentClass,
  onClose,
  children,
  width = "5xl",
}: {
  title: string;
  accentClass: string;
  onClose: () => void;
  children: React.ReactNode;
  width?: "2xl" | "5xl";
}) {
  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={clsx(
          "modal-box card-elevated w-full",
          width === "5xl" ? "max-w-5xl" : "max-w-2xl",
        )}
      >
        <h3 className={clsx("text-lg font-display font-semibold mb-4", accentClass)}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function DialogActions({
  submitLabel,
  submitting,
  submitDisabled,
  onClose,
}: {
  submitLabel: string;
  submitting: boolean;
  submitDisabled?: boolean;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-3 border-t border-base-300/60">
      <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
        Cancelar
      </button>
      <button
        type="submit"
        className="btn btn-primary btn-sm"
        disabled={submitting || submitDisabled}
      >
        {submitting && <span className="loading loading-spinner loading-xs" />}
        {submitLabel}
      </button>
    </div>
  );
}

type IvaRate = "16" | "8" | "0" | "exempt" | "custom";

const IVA_RATE_LABELS: Record<IvaRate, string> = {
  "16": "16% general",
  "8": "8% frontera",
  "0": "0% tasa cero",
  exempt: "Exento",
  custom: "Personalizado",
};

function computeIvaForRate(subtotalCents: number, rate: IvaRate): number {
  if (rate === "custom") return 0;
  const pct = rate === "16" ? 0.16 : rate === "8" ? 0.08 : 0;
  return Math.round(subtotalCents * pct);
}

/** Infiere la tasa al re-abrir una transacción del DB en modo edit. */
function inferIvaRate(subtotalCents: number, ivaCents: number, locked: boolean): IvaRate {
  if (locked) return "exempt";
  if (ivaCents === 0) {
    return "exempt";
  }
  if (subtotalCents <= 0) return "custom";
  const expectedAt16 = Math.round(subtotalCents * 0.16);
  const expectedAt8 = Math.round(subtotalCents * 0.08);
  // Tolerancia de 1 cent por redondeo.
  if (Math.abs(ivaCents - expectedAt16) <= 1) return "16";
  if (Math.abs(ivaCents - expectedAt8) <= 1) return "8";
  return "custom";
}

function AmountSection({
  control: _control,
  subtotal,
  iva,
  ivaRate,
  onSubtotal,
  onIva,
  onTotal,
  onIvaRateChange,
  ivaLocked,
  ivaLockedReason,
  totalError,
}: {
  control: Control<TransactionCreateInput>;
  subtotal: number;
  iva: number;
  ivaRate: IvaRate;
  onSubtotal: (v: number) => void;
  onIva: (v: number) => void;
  onTotal: (v: number) => void;
  onIvaRateChange: (rate: IvaRate) => void;
  ivaLocked?: boolean;
  ivaLockedReason?: string;
  totalError?: string;
}) {
  const isAutoCalc = ivaRate !== "custom";
  // Sin IVA: la tx tiene un único monto (impuestos al SAT, nóminas,
  // intereses, etc.). Colapsa a un solo input "Monto" — sin Tasa IVA,
  // sin IVA, sin Total duplicado.
  if (ivaLocked) {
    return (
      <div className="card-elevated p-4 bg-base-200/40 space-y-2">
        <Field
          label="Monto"
          required
          error={totalError}
          help={
            ivaLockedReason ? (
              <span className="text-xs text-base-content/55">{ivaLockedReason}</span>
            ) : null
          }
        >
          <CentsInput value={subtotal} onChange={onSubtotal} />
        </Field>
      </div>
    );
  }
  return (
    <div className="card-elevated p-4 bg-base-200/40">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Subtotal" required>
          <CentsInput value={subtotal} onChange={onSubtotal} />
        </Field>
        <Field label="Tasa IVA">
          <select
            className="select select-bordered w-full"
            value={ivaRate}
            onChange={(e) => onIvaRateChange(e.target.value as IvaRate)}
          >
            <option value="16">{IVA_RATE_LABELS["16"]}</option>
            <option value="8">{IVA_RATE_LABELS["8"]}</option>
            <option value="0">{IVA_RATE_LABELS["0"]}</option>
            <option value="exempt">{IVA_RATE_LABELS.exempt}</option>
            <option value="custom">{IVA_RATE_LABELS.custom}</option>
          </select>
        </Field>
        <Field
          label="IVA"
          help={
            isAutoCalc ? (
              <span className="text-xs text-base-content/55">Auto</span>
            ) : (
              <span className="text-xs text-base-content/55">Manual</span>
            )
          }
        >
          <CentsInput value={iva} onChange={onIva} readOnly={isAutoCalc} />
        </Field>
        <Field
          label="Total"
          error={totalError}
          help={
            <span className="text-xs text-base-content/55">
              Escríbelo y desglosamos subtotal + IVA
            </span>
          }
        >
          <CentsInput value={subtotal + iva} onChange={onTotal} />
        </Field>
      </div>
    </div>
  );
}

// ─── Field & CentsInput ───────────────────────────────────────────────────

function Field({
  label,
  error,
  help,
  required,
  children,
}: {
  label: string;
  error?: string;
  help?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="form-control w-full block">
      <div className="label py-1">
        <span className="label-text text-sm font-medium">
          {label}
          {required && <span className="text-error ml-0.5">*</span>}
        </span>
      </div>
      {children}
      {error && <p className="text-xs text-error mt-1.5 leading-relaxed">{error}</p>}
      {!error && help && (
        <p className="text-xs text-base-content/55 mt-1.5 leading-relaxed">{help}</p>
      )}
    </label>
  );
}

/**
 * Sub-form para asimilados a salarios (Art. 96 LISR).
 *
 * MODELO de datos:
 *   - tx.totalCents = NETO al trabajador (lo que sale del banco)
 *   - tx.subtotalCents = neto (iva=0 para asimilados)
 *   - tx.isrRetenidoCents = retención que la empresa entera al SAT aparte
 *   - tx.payrollUserId = FK al user (catálogo /admin/users) — opcional
 *   - tx.beneficiary* = snapshot de nombre/RFC/CURP al momento
 *
 * Bruto computado = neto + ISR retenido. La empresa deduce el bruto
 * (la API suma isr_retenido en computeSummary cuando is_asimilado=true).
 *
 * UX:
 *   - El usuario captura el NETO (= total tx, lo que sale del banco). El
 *     BRUTO y la retención ISR se computan por reverse de Art. 96.
 *   - Si overridea ISR manualmente, el bruto se recalcula simple
 *     (bruto = neto + isr) sin volver a invocar Art. 96.
 *   - Si elige un user del selector, beneficiary* se pre-rellenan.
 */
function AsimiladoSection({
  control,
  register,
  setValue,
}: {
  control: Control<TransactionCreateInput>;
  register: ReturnType<typeof useForm<TransactionCreateInput>>["register"];
  setValue: ReturnType<typeof useForm<TransactionCreateInput>>["setValue"];
}) {
  const isAsimilado = useWatch({ control, name: "isAsimilado" }) ?? false;
  const totalNet = useWatch({ control, name: "totalCents" }) ?? 0;
  const isr = useWatch({ control, name: "isrRetenidoCents" }) ?? 0;
  const bruto = totalNet + (isr ?? 0);
  // Sugerencia "honrando Art. 96" basada en el NETO actual: cuánto debería
  // ser el ISR si el bruto se calcula con la tarifa. Sirve para el botón
  // "Recalcular" que sincroniza ISR a la cifra exacta de SAT.
  const suggestedIsr = calcIsrAsimiladoMensual(calcBrutoFromNetoAsimilado(totalNet));

  // Catálogo de trabajadores — sólo users con isPayrollWorker=1. RFC y
  // CURP se pre-rellenan al seleccionar; el bruto SIEMPRE lo captura el
  // operador (varía por días no trabajados, bonos, ajustes).
  const workersQ = useQuery<{
    users: {
      id: string;
      name: string;
      rfc: string | null;
      curp: string | null;
      workerType: "asimilado" | "becario" | "honorarios" | null;
    }[];
  }>({
    enabled: isAsimilado,
    queryKey: ["users", "payroll"],
    queryFn: () => api.get("/users?payroll=1"),
    staleTime: 5 * 60_000,
  });

  // El picker setea `payrollUserId`; con eso ubicamos el worker_type para
  // decidir si IVA aplica. Becarios sí permiten capturar IVA cuando hay
  // factura; el resto fuerza IVA=0 (Art. 96 / honorarios sin IVA típico).
  const payrollUserId = useWatch({ control, name: "payrollUserId" });
  const selectedWorker = (workersQ.data?.users ?? []).find((w) => w.id === payrollUserId);
  const isBecario = selectedWorker?.workerType === "becario";

  // Cuando se activa el toggle con un NETO ya capturado y sin ISR, autollenar
  // ISR por reverse Art. 96. Si el usuario ya tenía ISR manual, respetar.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isAsimilado) return;
    if (totalNet <= 0) return;
    if (isr > 0) return;
    setValue("isrRetenidoCents", suggestedIsr, { shouldDirty: false });
  }, [isAsimilado, totalNet]);

  // Capturar NETO (= total tx) desde el sub-form: setea subtotal/total al
  // valor capturado y deriva ISR (Art. 96 reverso). El bruto queda como
  // valor calculado (display only).
  // Para becarios: NO forzamos IVA=0 — el operador captura si la factura
  // del programa lo incluye.
  const updateNeto = (n: number) => {
    const brutoReverse = calcBrutoFromNetoAsimilado(n);
    const newIsr = Math.max(0, brutoReverse - n);
    setValue("subtotalCents", n, { shouldDirty: true });
    setValue("totalCents", n, { shouldDirty: true });
    if (!isBecario) setValue("ivaCents", 0);
    setValue("isrRetenidoCents", newIsr, { shouldDirty: true });
  };

  // Override manual del ISR retenido: el NETO se mantiene fijo (lo escribió
  // el operador desde el banco). El bruto queda implícito = net + isr.
  const updateIsr = (newIsr: number) => {
    setValue("isrRetenidoCents", newIsr, { shouldDirty: true });
  };

  return (
    <div className="rounded-md border border-base-300/60 bg-base-200/30">
      <label className="cursor-pointer flex items-start gap-3 p-3">
        <input
          type="checkbox"
          className="toggle toggle-sm toggle-primary shrink-0 mt-0.5"
          {...register("isAsimilado")}
        />
        <div className="text-left min-w-0 flex-1">
          <div className="text-sm font-medium">Asimilado a salarios (Art. 96 LISR)</div>
          <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">
            El total de la tx = NETO al trabajador. El bruto y la retención van abajo. La empresa
            deduce el bruto (incluye la retención) automáticamente en reportes.
          </div>
        </div>
      </label>
      {isAsimilado && (
        <div className="border-t border-base-300/60 p-3 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <BeneficiarioPicker
              workers={workersQ.data?.users ?? []}
              control={control}
              setValue={setValue}
              register={register}
            />
            <Field label="RFC">
              <input
                className="input input-bordered w-full uppercase font-mono"
                placeholder="XAXX010101000"
                maxLength={13}
                {...register("beneficiaryRfc")}
              />
            </Field>
            <Field label="CURP">
              <input
                className="input input-bordered w-full uppercase font-mono"
                placeholder="XAXX010101HDFXXX01"
                maxLength={18}
                {...register("beneficiaryCurp")}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <Field
              label="Neto"
              help="Lo que recibe el trabajador (= total de la transferencia bancaria). El bruto se calcula reverso por Art. 96."
            >
              <CentsInput value={totalNet} onChange={updateNeto} />
            </Field>
            <Field
              label="ISR retenido"
              help={
                <span>
                  Auto por reverse Art. 96 LISR.{" "}
                  {totalNet > 0 && suggestedIsr !== isr && (
                    <button
                      type="button"
                      onClick={() => updateIsr(suggestedIsr)}
                      className="text-primary underline ml-1"
                    >
                      Recalcular ({formatMxnCents(suggestedIsr)})
                    </button>
                  )}
                </span>
              }
            >
              <CentsInput value={isr} onChange={updateIsr} />
            </Field>
            <div className="text-xs text-base-content/55 pb-3 space-y-0.5">
              <div>
                <span className="font-semibold text-base-content/75">Neto (= total tx)</span>{" "}
                <span className="font-mono tabular-nums text-success font-semibold">
                  {formatMxnCents(totalNet)}
                </span>
              </div>
              <div>
                <span className="font-semibold text-base-content/75">Retención SAT</span>{" "}
                <span className="font-mono tabular-nums text-warning">{formatMxnCents(isr)}</span>
              </div>
              <div>
                <span className="font-semibold text-base-content/75">Bruto</span>{" "}
                <span className="font-mono tabular-nums">{formatMxnCents(bruto)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Picker del beneficiario para asimilados. Combo: input de texto libre
 * con dropdown de matches contra el catálogo (users con
 * isPayrollWorker=1). Al elegir uno del dropdown se pre-rellenan los
 * snapshots fiscales (RFC, CURP) y el FK payroll_user_id. Si escriben
 * un nombre que no matchea ningún user, queda como entrada manual
 * (payrollUserId vacío).
 */
function BeneficiarioPicker({
  workers,
  control,
  setValue,
  register,
}: {
  workers: {
    id: string;
    name: string;
    rfc: string | null;
    curp: string | null;
    workerType: "asimilado" | "becario" | "honorarios" | null;
  }[];
  control: Control<TransactionCreateInput>;
  setValue: ReturnType<typeof useForm<TransactionCreateInput>>["setValue"];
  register: ReturnType<typeof useForm<TransactionCreateInput>>["register"];
}) {
  const beneficiaryName = useWatch({ control, name: "beneficiaryName" }) ?? "";
  const payrollUserId = useWatch({ control, name: "payrollUserId" }) ?? "";
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const query = beneficiaryName.trim().toLowerCase();
  const filtered: typeof workers = useMemo(() => {
    if (!query) return workers.slice(0, 8);
    return workers
      .filter(
        (w) =>
          w.name.toLowerCase().includes(query) || (w.rfc?.toLowerCase().includes(query) ?? false),
      )
      .slice(0, 8);
  }, [workers, query]);

  const pick = (w: (typeof workers)[number]) => {
    setValue("payrollUserId", w.id, { shouldDirty: true });
    setValue("beneficiaryName", w.name, { shouldDirty: true });
    if (w.rfc) setValue("beneficiaryRfc", w.rfc, { shouldDirty: true });
    if (w.curp) setValue("beneficiaryCurp", w.curp, { shouldDirty: true });
    setOpen(false);
  };

  return (
    <Field
      label="Beneficiario"
      help={
        payrollUserId ? (
          <span className="text-xs text-success">Trabajador del catálogo · RFC/CURP heredados</span>
        ) : (
          <span className="text-xs text-base-content/55">
            Elige uno del catálogo o escribe manual
          </span>
        )
      }
    >
      <div className="relative">
        <input
          className="input input-bordered w-full"
          placeholder="Nombre del trabajador"
          {...register("beneficiaryName", {
            onChange: () => {
              // Cualquier cambio manual rompe el match con el catálogo
              if (payrollUserId) setValue("payrollUserId", undefined, { shouldDirty: true });
              setOpen(true);
              setHighlight(0);
            },
          })}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (!open) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, filtered.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlight((h) => Math.max(0, h - 1));
            } else if (e.key === "Enter" && filtered[highlight]) {
              e.preventDefault();
              pick(filtered[highlight]);
            } else if (e.key === "Escape") {
              setOpen(false);
            }
          }}
        />
        {open && filtered.length > 0 && (
          <ul className="absolute z-30 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-base-100 border border-base-300 rounded-md shadow-lg py-1">
            {filtered.map((w, i) => (
              <li key={w.id}>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(w);
                  }}
                  onMouseEnter={() => setHighlight(i)}
                  className={clsx(
                    "w-full text-left px-3 py-2 text-sm",
                    i === highlight ? "bg-primary/10" : "hover:bg-base-200/60",
                  )}
                >
                  <div className="font-medium truncate flex items-center gap-2">
                    {w.name}
                    {w.workerType && w.workerType !== "asimilado" && (
                      <span
                        className={clsx(
                          "badge badge-xs",
                          w.workerType === "becario" ? "badge-accent" : "badge-ghost",
                        )}
                      >
                        {w.workerType}
                      </span>
                    )}
                  </div>
                  {w.rfc && <div className="text-xs text-base-content/55 font-mono">{w.rfc}</div>}
                </button>
              </li>
            ))}
          </ul>
        )}
        {open && filtered.length === 0 && query && (
          <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-md shadow-lg px-3 py-2 text-xs text-base-content/55">
            Sin match en catálogo — se guarda como entrada manual
          </div>
        )}
      </div>
    </Field>
  );
}

/**
 * Sub-form para pagos al SAT. Marca la tx como pago a la autoridad
 * (enteros mensuales, retenciones, DPA) y captura la línea de captura
 * para referencia en reportes.
 *
 * El desglose IVA/ISR/otros se hace con splits (usando las categorías
 * Impuestos — ISR / IVA / otros que ya existen) para no duplicar columnas
 * en la tabla. El padre conserva el bruto total de lo que salió del banco.
 */
function SatPaymentSection({
  control,
  register,
  setValue,
  splits,
  onSplitsChange,
  parentTotalCents,
}: {
  control: Control<TransactionCreateInput>;
  register: ReturnType<typeof useForm<TransactionCreateInput>>["register"];
  setValue: ReturnType<typeof useForm<TransactionCreateInput>>["setValue"];
  splits: SplitDraftLocal[];
  onSplitsChange: (next: SplitDraftLocal[]) => void;
  parentTotalCents: number;
}) {
  const isSatPayment = useWatch({ control, name: "isSatPayment" }) ?? false;
  const txDate = useWatch({ control, name: "date" }) ?? "";
  const satPeriod = useWatch({ control, name: "satPeriod" }) ?? "";

  // Default inteligente: cuando se activa el toggle y satPeriod está vacío,
  // sugerir el mes anterior al date de la tx (la declaración mensual SAT
  // del periodo X se paga el 17 del mes X+1).
  useEffect(() => {
    if (!isSatPayment || satPeriod || !/^\d{4}-\d{2}-\d{2}$/.test(txDate)) return;
    const [y, m] = txDate.split("-").map(Number);
    if (!y || !m) return;
    const prev = new Date(y, m - 2, 1); // m-2 porque Date usa 0-indexed
    const yyyymm = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
    setValue("satPeriod", yyyymm, { shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSatPayment, txDate]);

  /**
   * Genera 5 splits vacíos modelando los conceptos del recibo SAT:
   *   1. ISR propio (RESICO simplificado de confianza)
   *   2. ISR ret. asimilados (Art. 96 LISR)
   *   3. ISR ret. salarios (Capítulo I)
   *   4. IVA simplificado de confianza
   *   5. Otros (recargos, actualización, DPA, multas — sumados acá)
   *
   * Montos arrancan en 0; el usuario los captura del recibo SAT. La
   * suma debe igualar el bruto — el SplitsSection muestra el remanente
   * en vivo. Los recargos del recibo SAT pueden ir cada uno en su
   * concepto, o todos juntos en "Otros" — preferencia del usuario.
   */
  const generarDesglose = () => {
    const next: SplitDraftLocal[] = [
      {
        categoryId: "cat_impuestos_isr_propio",
        projectId: null,
        companyId: null,
        description: "ISR simplificado de confianza (RESICO PM)",
        subtotalCents: 0,
        ivaCents: 0,
        totalCents: 0,
      },
      {
        categoryId: "cat_impuestos_isr_asimilados",
        projectId: null,
        companyId: null,
        description: "ISR retenciones por asimilados a salarios",
        subtotalCents: 0,
        ivaCents: 0,
        totalCents: 0,
      },
      {
        categoryId: "cat_impuestos_isr_salarios",
        projectId: null,
        companyId: null,
        description: "ISR retenciones por salarios",
        subtotalCents: 0,
        ivaCents: 0,
        totalCents: 0,
      },
      {
        categoryId: "cat_impuestos_iva",
        projectId: null,
        companyId: null,
        description: "IVA simplificado de confianza",
        subtotalCents: 0,
        ivaCents: 0,
        totalCents: 0,
      },
      {
        categoryId: "cat_impuestos_otros",
        projectId: null,
        companyId: null,
        description: "Recargos / actualización / DPA / multas",
        subtotalCents: parentTotalCents,
        ivaCents: 0,
        totalCents: parentTotalCents,
      },
    ];
    onSplitsChange(next);
  };

  const hasExistingSplits = splits.length > 0;

  return (
    <div className="rounded-md border border-base-300/60 bg-base-200/30">
      <label className="cursor-pointer flex items-start gap-3 p-3">
        <input
          type="checkbox"
          className="toggle toggle-sm toggle-primary shrink-0 mt-0.5"
          {...register("isSatPayment")}
        />
        <div className="text-left min-w-0 flex-1">
          <div className="text-sm font-medium">Pago al SAT</div>
          <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">
            Entero mensual, retenciones, DPA. Desglose IVA / ISR / otros vía splits con las
            categorías Impuestos — *.
          </div>
        </div>
      </label>
      {isSatPayment && (
        <div className="border-t border-base-300/60 p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field
            label="Periodo declarado"
            help={`Mes que se está declarando, NO el mes del pago. Default: mes anterior a ${txDate || "la fecha de la tx"}.`}
          >
            <input
              type="month"
              className="input input-bordered w-full"
              {...register("satPeriod")}
            />
          </Field>
          <Field
            label="Línea de captura"
            help="Referencia del pago SAT (~20 caracteres alfanuméricos). Sirve para conciliar contra la declaración del Impuestos."
          >
            <input
              className="input input-bordered w-full uppercase font-mono"
              placeholder="0123456789ABCDEF0123"
              maxLength={40}
              {...register("satLineaCaptura")}
            />
          </Field>
          <div className="md:col-span-2 flex items-center gap-3 flex-wrap pt-2 border-t border-base-300/60">
            <button
              type="button"
              onClick={generarDesglose}
              disabled={parentTotalCents <= 0}
              className="btn btn-sm btn-outline btn-primary"
            >
              {hasExistingSplits
                ? "Regenerar desglose IVA / ISR / Otros"
                : "Generar desglose IVA / ISR / Otros"}
            </button>
            <span className="text-xs text-base-content/55 leading-tight">
              Crea 5 splits modelando los conceptos del recibo SAT: ISR propio, ISR ret. asimilados,
              ISR ret. salarios, IVA, Otros (recargos/DPA). Captura los montos del recibo en cada
              uno — la suma debe coincidir con el bruto.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Sub-form para ingresos cobrados vía plataforma de pago (Stripe,
 * PayPal, Mercado Pago, Conekta, etc.). La plataforma toma una
 * comisión antes de liquidar al banco.
 *
 * Fiscalmente lo correcto es:
 *   - El CFDI emitido al cliente vale el bruto (total del ingreso)
 *   - La comisión es un gasto DEDUCIBLE aparte
 *   - Si la plataforma emite CFDI MX → facturado + IVA acreditable
 *   - Si no (Stripe US, PayPal IE) → deducible_sin_factura
 *
 * Cuando active=true al guardar, además del ingreso se crea una tx
 * gasto pareja con categoría cat_comisiones_plataforma. Solo se
 * muestra en modo CREACIÓN de ingreso (no edit) para evitar gestionar
 * relaciones padre-hijo entre txs.
 */
const PLATFORMS = ["Stripe", "PayPal", "Mercado Pago", "Conekta", "Clip", "Otra"];

function PlatformCommissionSection({
  value,
  onChange,
  ingresoTotalCents,
}: {
  value: {
    active: boolean;
    platform: string;
    commissionCents: number;
    hasCfdi: boolean;
  };
  onChange: (next: {
    active: boolean;
    platform: string;
    commissionCents: number;
    hasCfdi: boolean;
  }) => void;
  ingresoTotalCents: number;
}) {
  const net = ingresoTotalCents - value.commissionCents;
  return (
    <div className="rounded-md border border-base-300/60 bg-base-200/30">
      <label className="cursor-pointer flex items-start gap-3 p-3">
        <input
          type="checkbox"
          className="toggle toggle-sm toggle-primary shrink-0 mt-0.5"
          checked={value.active}
          onChange={(e) => onChange({ ...value, active: e.target.checked })}
        />
        <div className="text-left min-w-0 flex-1">
          <div className="text-sm font-medium">Cobrado vía plataforma de pago</div>
          <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">
            El CFDI sigue siendo por el bruto; la comisión es un gasto separado deducible. Al
            guardar se crea automáticamente la tx-pareja con la categoría Comisiones de plataforma.
          </div>
        </div>
      </label>
      {value.active && (
        <div className="border-t border-base-300/60 p-3 grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <Field label="Plataforma">
            <select
              className="select select-bordered w-full"
              value={value.platform}
              onChange={(e) => onChange({ ...value, platform: e.target.value })}
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Comisión (lo que se quedó la plataforma)">
            <CentsInput
              value={value.commissionCents}
              onChange={(v) => onChange({ ...value, commissionCents: v })}
            />
          </Field>
          <label className="cursor-pointer flex items-center gap-2 text-sm py-1">
            <input
              type="checkbox"
              className="checkbox checkbox-sm checkbox-primary"
              checked={value.hasCfdi}
              onChange={(e) => onChange({ ...value, hasCfdi: e.target.checked })}
            />
            <span>
              La plataforma emite CFDI mexicano{" "}
              <span className="text-xs text-base-content/55">
                (Stripe MX sí; Stripe US/IE, PayPal IE no)
              </span>
            </span>
          </label>
          <div className="text-xs text-base-content/55 space-y-0.5 pb-1">
            <div>
              <span className="font-semibold text-base-content/75">Bruto CFDI</span>{" "}
              <span className="font-mono tabular-nums">{formatMxnCents(ingresoTotalCents)}</span>
            </div>
            <div>
              <span className="font-semibold text-base-content/75">Comisión</span>{" "}
              <span className="font-mono tabular-nums text-error">
                {formatMxnCents(value.commissionCents)}
              </span>
              {value.hasCfdi && value.commissionCents > 0 && (
                <span className="text-base-content/45">
                  {" "}
                  (IVA acreditable{" "}
                  {formatMxnCents(value.commissionCents - Math.round(value.commissionCents / 1.16))}
                  )
                </span>
              )}
            </div>
            <div>
              <span className="font-semibold text-base-content/75">Neto al banco</span>{" "}
              <span className="font-mono tabular-nums text-success font-semibold">
                {formatMxnCents(Math.max(0, net))}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Editor de splits (sub-ingresos / sub-gastos). El padre conserva el total
 * bruto; los splits reparten ese total por categoría / proyecto /
 * contraparte. La suma de splits debe coincidir EXACTAMENTE con el total
 * del padre — se muestra un indicador de remanente para guiar al usuario.
 *
 * Cuando splits.length === 0, no se envía nada (la tx queda atribuida al
 * padre como hasta ahora).
 */
interface SplitDraftLocal {
  id?: string;
  categoryId: string | null;
  projectId: string | null;
  companyId: string | null;
  description: string | null;
  subtotalCents: number;
  ivaCents: number;
  totalCents: number;
}

function SplitsSection({
  splits,
  onChange,
  parentTotalCents,
  parentSubtotalCents,
  parentIvaCents,
  kind,
  categories,
  projects,
}: {
  splits: SplitDraftLocal[];
  onChange: (next: SplitDraftLocal[]) => void;
  parentTotalCents: number;
  parentSubtotalCents: number;
  parentIvaCents: number;
  kind: "ingreso" | "gasto";
  categories: Category[];
  projects: Array<{ id: string; name: string; type: string }>;
}) {
  const sumTotalCents = splits.reduce((s, x) => s + x.totalCents, 0);
  const remainderCents = parentTotalCents - sumTotalCents;
  const inBalance = splits.length === 0 || remainderCents === 0;

  // Tasa efectiva del padre (iva / subtotal). Si no hay IVA en el padre,
  // la tasa es 0 y los splits no llevan IVA. Si hay, calculamos IVA del
  // split automático al cambiar subtotal/total.
  const parentIvaRate = parentSubtotalCents > 0 ? parentIvaCents / parentSubtotalCents : 0;

  const breakdownByTotal = (totalCents: number) => {
    if (parentIvaRate === 0) return { sub: totalCents, iva: 0 };
    const sub = Math.round(totalCents / (1 + parentIvaRate));
    return { sub, iva: totalCents - sub };
  };
  const ivaFromSubtotal = (sub: number) => Math.round(sub * parentIvaRate);

  const addSplit = () => {
    // El nuevo split arranca con el remanente desglosado por la tasa del
    // padre, así el usuario solo ajusta categoría/proyecto sin recalcular.
    const remainTotal = Math.max(0, parentTotalCents - sumTotalCents);
    const { sub, iva } = breakdownByTotal(remainTotal);
    onChange([
      ...splits,
      {
        categoryId: null,
        projectId: null,
        companyId: null,
        description: null,
        subtotalCents: sub,
        ivaCents: iva,
        totalCents: remainTotal,
      },
    ]);
  };

  const update = (i: number, patch: Partial<SplitDraftLocal>) => {
    onChange(splits.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  };

  // Una categoría con iva_applicable=false (ej. Impuestos — *) NO debe
  // tener IVA en su split. El monto del split ES el impuesto en sí, no
  // un gasto con IVA acreditable encima.
  const catById = new Map(categories.map((c) => [c.id, c]));
  const splitNoIva = (i: number) => {
    const s = splits[i];
    if (!s || !s.categoryId) return false;
    const cat = catById.get(s.categoryId);
    return cat ? !cat.ivaApplicable : false;
  };

  const updateSubtotal = (i: number, sub: number) => {
    // Si la categoría del split no aplica IVA, el subtotal = total y el
    // IVA queda en 0 sin importar la tasa del padre.
    if (splitNoIva(i)) {
      update(i, { subtotalCents: sub, ivaCents: 0, totalCents: sub });
      return;
    }
    const iva = ivaFromSubtotal(sub);
    update(i, { subtotalCents: sub, ivaCents: iva, totalCents: sub + iva });
  };
  const updateIva = (i: number, iva: number) => {
    // Override manual del IVA — total se ajusta pero el subtotal queda
    // intacto. Útil si la parte tiene una tasa distinta (ej. tasa 0%).
    const s = splits[i]!;
    update(i, { ivaCents: iva, totalCents: s.subtotalCents + iva });
  };
  /**
   * Al cambiar la categoría: si la nueva es no-iva (impuestos), fuerza
   * IVA a 0 y total = subtotal. Si era no-iva y ahora es con-iva,
   * recalcula IVA con la tasa del padre. Evita estados inconsistentes.
   */
  const updateCategory = (i: number, categoryId: string | null) => {
    const s = splits[i]!;
    const newCat = categoryId ? catById.get(categoryId) : undefined;
    const newCatNoIva = newCat ? !newCat.ivaApplicable : false;
    if (newCatNoIva) {
      // Snap a IVA=0; preserva subtotal como total.
      update(i, {
        categoryId,
        ivaCents: 0,
        totalCents: s.subtotalCents,
      });
    } else {
      const iva = ivaFromSubtotal(s.subtotalCents);
      update(i, {
        categoryId,
        ivaCents: iva,
        totalCents: s.subtotalCents + iva,
      });
    }
  };

  return (
    <div className="rounded-md border border-base-300/60 bg-base-200/30">
      <div className="flex items-center justify-between gap-2 p-3">
        <div className="min-w-0">
          <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
            Desglose en sub-cuentas
            {splits.length > 0 && (
              <span className="badge badge-xs badge-ghost">{splits.length}</span>
            )}
            {splits.length > 0 &&
              (inBalance ? (
                <span className="badge badge-xs badge-success">balanceado</span>
              ) : (
                <span className="badge badge-xs badge-warning">
                  faltan {formatMxnCents(remainderCents)}
                </span>
              ))}
          </div>
          <div className="text-xs text-base-content/55 leading-relaxed mt-0.5">
            Reparte este movimiento en sub-cuentas con categoría / proyecto / contraparte propios.
            La suma debe igualar el total del padre ({formatMxnCents(parentTotalCents)}).
          </div>
        </div>
        <button
          type="button"
          onClick={addSplit}
          className="btn btn-ghost btn-xs"
          disabled={parentTotalCents === 0}
        >
          + Agregar
        </button>
      </div>

      {splits.length > 0 && (
        <div className="border-t border-base-300/60 p-3 space-y-2">
          {splits.map((s, i) => {
            const noIva = splitNoIva(i);
            return (
              <div
                key={s.id ?? i}
                className="rounded-md border border-base-300/40 bg-base-100 p-2.5 space-y-2"
              >
                <div className={clsx("grid gap-2", noIva ? "" : "sm:grid-cols-3")}>
                  <Field label={`#${i + 1} · Monto`}>
                    <CentsInput value={s.subtotalCents} onChange={(v) => updateSubtotal(i, v)} />
                  </Field>
                  {!noIva && (
                    <>
                      <Field label="IVA">
                        <CentsInput value={s.ivaCents} onChange={(v) => updateIva(i, v)} />
                      </Field>
                      <Field label="Total">
                        <CentsInput value={s.totalCents} onChange={() => undefined} readOnly />
                      </Field>
                    </>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Field label="Categoría">
                    <select
                      className="select select-bordered select-sm w-full"
                      value={s.categoryId ?? ""}
                      onChange={(e) =>
                        updateCategory(i, e.target.value === "" ? null : e.target.value)
                      }
                    >
                      <option value="">— hereda del padre —</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Proyecto">
                    <select
                      className="select select-bordered select-sm w-full"
                      value={s.projectId ?? ""}
                      onChange={(e) =>
                        update(i, { projectId: e.target.value === "" ? null : e.target.value })
                      }
                    >
                      <option value="">— hereda del padre —</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Empresa">
                    <CompanyPicker
                      value={s.companyId ?? undefined}
                      onChange={(v) => update(i, { companyId: v ?? null })}
                      onlySuppliers={kind === "gasto"}
                      onlyClients={kind === "ingreso"}
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
                  <Field label="Descripción">
                    <input
                      className="input input-bordered input-sm w-full"
                      value={s.description ?? ""}
                      onChange={(e) =>
                        update(i, { description: e.target.value === "" ? null : e.target.value })
                      }
                      placeholder="Detalle de esta parte"
                    />
                  </Field>
                  <button
                    type="button"
                    onClick={() => onChange(splits.filter((_, idx) => idx !== i))}
                    className="btn btn-ghost btn-sm text-error"
                    title="Quitar sub-cuenta"
                  >
                    <Trash2 className="size-3.5" /> Quitar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CentsInput({
  value,
  onChange,
  readOnly,
}: {
  value: number;
  onChange: (cents: number) => void;
  readOnly?: boolean;
}) {
  const [text, setText] = useState((value / 100).toFixed(2));

  // Sincroniza text cuando value cambia desde afuera (ej. "Calcular 16%" o
  // setValue desde otro campo). Sin esto, el text interno se queda con el
  // valor viejo del usuario y el form muestra un total desincronizado.
  // Solo reescribe si el text actual no representa el value — así no se
  // estorba mientras el usuario está typeando "12." o similar.
  useEffect(() => {
    const cleaned = text.replace(/,/g, "").trim();
    const parsed = parseFloat(cleaned);
    const cents = Number.isNaN(parsed) ? 0 : Math.round(parsed * 100);
    if (cents !== value) {
      setText((value / 100).toFixed(2));
    }
  }, [value, text]);

  return (
    <div className="relative">
      <span
        className={clsx(
          "absolute left-3 top-1/2 -translate-y-1/2 text-sm",
          readOnly ? "text-base-content/35" : "text-base-content/55",
        )}
      >
        $
      </span>
      <input
        type="text"
        className={clsx(
          "input input-bordered w-full pl-7 font-mono tabular-nums text-right",
          readOnly && "bg-base-200 text-base-content/55 cursor-not-allowed",
        )}
        value={readOnly ? (value / 100).toFixed(2) : text}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        aria-disabled={readOnly}
        onChange={(e) => {
          if (readOnly) return;
          const v = e.target.value;
          setText(v);
          const num = parseFloat(v);
          if (!Number.isNaN(num)) onChange(Math.round(num * 100));
        }}
        onBlur={() => {
          const n = parseFloat(text);
          if (!Number.isNaN(n)) setText(n.toFixed(2));
        }}
      />
    </div>
  );
}
